import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/ProfilesManager.css";
import { getApiUrl, getCsrfToken } from "../utils/apiHelper";
import Header from "./Header";
import Sidebar from "./Sidebar";
import PleaseWait from "../utils/PleaseWait";
import StatusResultModal from "./StatusResultModal";
import ConfirmModal from "./ConfirmModal";
import { useSidebar } from "../App";
import { categoryLabels, subcategoryLabels, subcategoryLabelsMap, allPossibleCategories, paramLabels, profileTypeLabels } from "../utils/categoryLabels";
import { getAllowedTargetTypes } from "../utils/targets";
const FIXED_RETRY_ATTEMPTS = 2;
const CONCURRENT_TESTING_MAX = 10;
const PROMPTS_PER_SEC_MAX = 50;
const CONCURRENT_TESTING_OPTIONS = Array.from({ length: CONCURRENT_TESTING_MAX }, (_, i) => i + 1);
const PROMPTS_PER_SEC_OPTIONS = Array.from({ length: PROMPTS_PER_SEC_MAX }, (_, i) => i + 1);
const PROMPTS_CATEGORIES = ['prompts_testing'];
const LEGACY_PROMPTS_GRID_CATEGORIES = [
    'input_output_risks',
    'behavioural_threats',
    'prompt_injection',
];
const REGULATION_COMPLIANCE_GRID_CATEGORIES = [
    'rc_gdpr',
    'rc_owasp_llm',
    'rc_hipaa',
    'rc_pci_dss',
    'rc_eu_ai_act',
    'rc_nist_ai_rmf',
    'rc_soc2'
];

// Format date for display
const formatDate = (dateString, { locale, notAvailable, invalidDate } = {}) => {
    if (!dateString) return notAvailable || 'N/A';
    try {
        const date = new Date(dateString);
        const loc = locale || undefined;
        return date.toLocaleDateString(loc) + ' ' + date.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
        console.error("Error formatting date:", error);
        return invalidDate || 'Invalid Date';
    }
};

// Get current theme
const getCurrentTheme = () => {
    return document.body.getAttribute('data-theme') || 'dark';
};

const profileTypeOptionStyle = (selected, allowed) => {
    const light = getCurrentTheme() === 'light';
    return {
        display: 'flex',
        alignItems: 'center',
        padding: '16px',
        borderRadius: '8px',
        border: `2px solid ${selected ? '#2563eb' : (light ? '#d1d5db' : '#374151')}`,
        background: selected
            ? (light ? '#dbeafe' : '#1e3a8a')
            : (light ? '#f9fafb' : '#111827'),
        color: light ? '#111827' : '#ffffff',
        cursor: allowed ? 'pointer' : 'not-allowed',
        opacity: allowed ? 1 : 0.5,
        transition: 'all 0.2s ease',
        boxShadow: selected ? '0 0 0 3px rgba(37,99,235,0.25)' : 'none'
    };
};

// Validation helper functions
const isValidAlphanumeric = (value) => {
    // Only allow alphanumeric characters and spaces
    return /^[a-zA-Z0-9\s]*$/.test(value);
};

const startsWithNumber = (value) => {
    // Check if value starts with a number
    return /^\d/.test(value.trim());
};

const isOnlyNumbers = (value) => {
    // Check if value contains only numbers (after removing spaces)
    const trimmed = value.trim();
    return trimmed.length > 0 && /^\d+$/.test(trimmed);
};

const validateProfileNameOrDescription = (value) => {
    if (!value || value.trim() === '') {
        return { isValid: true, errorKey: null }; // Empty is handled separately
    }

    if (!isValidAlphanumeric(value)) {
        return {
            isValid: false,
            errorKey: 'validation.alphanumericOnly'
        };
    }

    if (startsWithNumber(value)) {
        return {
            isValid: false,
            errorKey: 'validation.cannotStartWithNumber'
        };
    }

    if (isOnlyNumbers(value)) {
        return {
            isValid: false,
            errorKey: 'validation.cannotBeOnlyNumbers'
        };
    }

    return { isValid: true, errorKey: null };
};

// Default params for llm_dos and mcp_agent subcategories (shared by normalizeConfig and toggle handlers)
const getDefaultParams = (cat, sub) => {
    if (cat === 'llm_dos') {
        if (sub === 'resource_exhaustion_dos') return { concurrent_requests: 200, test_intensity: 'normal' };
        if (sub === 'cost_amplification') return { number_of_cost_requests: 50 };
        if (sub === 'service_disruption') return { sustained_duration: 300, sustained_rps: 20 };
        if (sub === 'memory_exhaustion') return { payload_size: 20, number_of_memory_requests: 30 };
        if (sub === 'token_overflow') return { max_context_length: 128000 };
        if (sub === 'rate_limit_bypass') return { test_strategy: 'parameter_mutation' };
        if (sub === 'test_context_overflow') return { phases: 10 };
        if (sub === 'test_prompt_injection_dos') return { injection_type: 'direct' };
        if (sub === 'test_repetitive_requests') return { pattern_type: 'identical' };
        if (sub === 'test_session_hijacking_dos') return { sessions_per_ip: 5 };
        if (sub === 'test_burst_attacks') return { burst_size: 50, interval: 2 };
        if (sub === 'test_slowloris_ai') return { chunks: 5, delay: 2 };
        if (sub === 'test_tool_execution_abuse') return { tool_calls: 20 };
        if (sub === 'test_model_poisoning_dos') return { poisoning_attempts: 10 };
    } else if (cat === 'mcp_agent') {
        if (sub === 'resource_exhaustion_mcpa') return { concurrent_resource_requests: 160 };
        if (sub === 'protocol_violation') return { number_of_protocol_requests: 60 };
        if (sub === 'tool_abuse') return { number_of_tool_requests: 200 };
        if (sub === 'memory_leak') return { number_of_iterations: 20 };
        if (sub === 'session_hijack') return { number_of_sessions: 40 };
        if (sub === 'context_overflow') return { context_overflow_count: 0 };
        if (sub === 'malicious_tool_calls') return { number_of_malicious_requests: 100 };
        if (sub === 'test_path_traversal') return { path_trav_attacks: 20 };
        if (sub === 'test_command_injection') return { cmd_inj_attacks: 20 };
        if (sub === 'test_sql_injection') return { sql_inj_attacks: 20 };
        if (sub === 'test_data_exfiltration') return { data_exfil_attacks: 20 };
        if (sub === 'test_prompt_injection_mcpa') return { prompt_inj_attacks: 20 };
        if (sub === 'test_auth_bypass') return { auth_bypass_attacks: 20 };
        if (sub === 'test_malicious_content') return { mal_cont_attacks: 20 };
        if (sub === 'test_session_hijacking_mcpa') return { session_hijack_attacks: 20 };
        if (sub === 'test_tool_misuse') return { tool_misuse_attacks: 20 };
        if (sub === 'test_generic_injection') return { gen_inj_attacks: 20 };
        if (sub === 'test_resource_exhaustion') return { rsc_ex_attacks: 10 };
        if (sub === 'test_benign_requests') return { benign_reqs_num: 20 };
    } else if (cat === 'adv_agentic_ai_attacks') {
        if (sub === 'adv_crescendo') return {
            crescendo_prompt: '',
            stages: 0,
            requests_per_stage: 0,
            intensity_multiplier: 1.0
        };
        if (sub === 'adv_trolley') return {
            trolley_prompt: '',
            num_dilemmas: 0,
            recursion_depth: 0,
            complexity_level: 'high'
        };

        if (sub === 'adv_converter') return {
            base64_conv: false,
            single_char_conv: false,
            repeat_conv: false,
            caesar_conv: false,
            leet_conv: false,
            unicode_conv: false
        };
    }
    return null;
};

// Null params for a subcategory (same keys as defaults, values null)
const getNullParams = (cat, sub) => {
    const d = getDefaultParams(cat, sub);
    return d ? Object.fromEntries(Object.keys(d).map((k) => [k, null])) : null;
};

const readThrottleSetting = (raw, fallback, max) => {
    const clamp = (n) => Math.min(max, Math.max(1, n));
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        const value = Number(raw.value);
        return {
            auto_select: raw.auto_select !== false,
            value: Number.isInteger(value) ? clamp(value) : fallback,
        };
    }
    const asNumber = Number(raw);
    if (raw != null && raw !== '' && Number.isInteger(asNumber)) {
        return { auto_select: false, value: clamp(asNumber) };
    }
    return { auto_select: true, value: fallback };
};

const throttleSelectValue = (setting) => (
    setting?.auto_select !== false ? 'auto' : String(setting?.value ?? 1)
);

export default function ProfilesManager() {
    const { sidebarCollapsed } = useSidebar();
    const { t, i18n } = useTranslation("profiles");
    const tabLabels = [
        t("tabs.profileType"),
        t("tabs.prompts"),
        t("tabs.advancedMultiAttacks"),
        t("tabs.documents"),
        t("tabs.aiModelAttackSurface"),
        t("tabs.aiDos"),
        t("tabs.advanceAgentic"),
        t("tabs.intrusiveMcp"),
        t("tabs.safeMcp"),
        t("tabs.rto"),
        t("tabs.regulation"),
        t("tabs.summary")
    ];
    // Direct category map structure (no conversion needed)
    const defaultCategoryMap = {
        "prompts_testing": {
            enabled: false
        },
        "regulation_compliance_testing": {
            enabled: false
        },
        "malicious_doc_uploads": {
            enabled: false
        },
        "llm_api_mis_use_abuse": {
            enabled: false,
            sub: {
                "anomalous_access": { enabled: false },
                "dos_rate_limit": { enabled: false },
                "abuse_misuse": { enabled: false },
                "mitm": { enabled: false },
                "dos_burst": { enabled: false },
                "invalid_api": { enabled: false },
                "api_key_token_abuse": { enabled: false },
            }
        },
        "llm_dos": {
            enabled: false,
            sub: {
                "resource_exhaustion_dos": { enabled: false },
                "cost_amplification": { enabled: false },
                "service_disruption": { enabled: false },
                "memory_exhaustion": { enabled: false },
                "token_overflow": { enabled: false },
                "rate_limit_bypass": { enabled: false },
                "test_context_overflow": { enabled: false },
                "test_prompt_injection_dos": { enabled: false },
                "test_repetitive_requests": { enabled: false },
                "test_session_hijacking_dos": { enabled: false },
                "test_burst_attacks": { enabled: false },
                "test_slowloris_ai": { enabled: false },
                "test_tool_execution_abuse": { enabled: false },
                "test_model_poisoning_dos": { enabled: false }
            }
        },
        "mcp_agent": {
            enabled: false,
            sub: {
                "resource_exhaustion_mcpa": { enabled: false },
                "protocol_violation": { enabled: false },
                "tool_abuse": { enabled: false },
                "memory_leak": { enabled: false },
                "session_hijack": { enabled: false },
                "context_overflow": { enabled: false },
                "malicious_tool_calls": { enabled: false },
                "test_path_traversal": { enabled: false },
                "test_command_injection": { enabled: false },
                "test_sql_injection": { enabled: false },
                "test_data_exfiltration": { enabled: false },
                "test_prompt_injection_mcpa": { enabled: false },
                "test_auth_bypass": { enabled: false },
                "test_malicious_content": { enabled: false },
                "test_session_hijacking_mcpa": { enabled: false },
                "test_tool_misuse": { enabled: false },
                "test_generic_injection": { enabled: false },
                "test_resource_exhaustion": { enabled: false },
                "test_benign_requests": { enabled: false }
            }
        },
        "safe_mcp_tests": {
            enabled: false,
            sub: {
                "safe_mcp_protocol": { enabled: false },
                "safe_mcp_transport": { enabled: false },
                "safe_mcp_tools_disc": { enabled: false },
                "safe_mcp_tools_sem": { enabled: false },
                "safe_mcp_robust": { enabled: false },
                "safe_mcp_ops": { enabled: false },
                "safe_mcp_auth": { enabled: false },
                "safe_mcp_rate_limit": { enabled: false },
                "safe_mcp_concurrency": { enabled: false },
                "safe_mcp_session_cancel": { enabled: false },
                "safe_mcp_pages": { enabled: false },
                "safe_mcp_stream": { enabled: false },
                "safe_mcp_encoding": { enabled: false },
                "safe_mcp_schema": { enabled: false },
                "safe_mcp_input_bounds": { enabled: false },
                "safe_mcp_unknown_methods": { enabled: false },
                "safe_mcp_resources": { enabled: false },
                "safe_mcp_prompt_handling": { enabled: false },
                "safe_mcp_observability": { enabled: false },
                "safe_mcp_side_effects": { enabled: false },
                "safe_mcp_baseline": { enabled: false }
            }
        },
        "rto_tests": {
            enabled: false,
            average_response_time: "overall",
            sub: {
                "reg_size_prompts": { enabled: false },
                "mid_size_prompts": { enabled: false },
                "large_size_prompts": { enabled: false },
                "doc_upload_tests": { enabled: false }
            }
        },
        "adv_agentic_ai_attacks": {
            enabled: false,
            sub: {
                "adv_crescendo": { enabled: false },
                "adv_trolley": { enabled: false },
                "adv_frame": { enabled: false },
                "adv_rec_pattern": { enabled: false },
                "adv_converter": { enabled: false }
            }
        },
        "adv_multi_attacks": {
            enabled: false,
            sub: {
                "ama_backdoor": { enabled: false },
                "ama_jb_classic": { enabled: false },
                "ama_jb_contextual": { enabled: false },
                "ama_decision_fatigue": { enabled: false },
                "ama_crisis_manip": { enabled: false },
                "ama_collusion": { enabled: false },
                "ama_resource_exhaustion": { enabled: false },
                "ama_jb_reverse": { enabled: false },
                "ama_safe_boundary": { enabled: false },
                "ama_jb_sophisticated": { enabled: false },
                "ama_info_extraction": { enabled: false },
                "ama_jb_token_manip": { enabled: false },
                "ama_tool_exploit": { enabled: false }
            }
        }
    };

    const TEST_FILE_BUCKET_KEYS = [
        'docs',
        'prompts',
        'rc_prompts',
        'llm',
        'mcp',
        'dos',
        'rto',
        'frame',
        'custom_decision_tree',
    ];

    const INPUT_FILE_METHODS = ['internal', 'uploaded', 'generated'];

    const getDefaultInputFiles = () => {
        const out = {};
        TEST_FILE_BUCKET_KEYS.forEach((key) => {
            out[key] = { internal: [], generated: [], uploaded: [] };
        });
        return out;
    };

    const normalizeInputFilesBucket = (bucket) => {
        const out = { internal: [], generated: [], uploaded: [] };
        if (!bucket || typeof bucket !== 'object') return out;
        INPUT_FILE_METHODS.forEach((method) => {
            if (Array.isArray(bucket[method])) {
                out[method] = [...bucket[method]];
            }
        });
        return out;
    };

    const normalizeInputFiles = (inputFiles) => {
        const out = getDefaultInputFiles();
        if (!inputFiles || typeof inputFiles !== 'object') return out;
        TEST_FILE_BUCKET_KEYS.forEach((key) => {
            out[key] = normalizeInputFilesBucket(inputFiles[key]);
        });
        return out;
    };

    const getInputFilesFromProfileConfig = (config) =>
        normalizeInputFiles(config?.input_files);

    const getFileEntryKey = (method, name) => `${method}:${name}`;

    const parseFileEntryKey = (key) => {
        const idx = key.indexOf(':');
        if (idx === -1) return { method: 'internal', name: key };
        return { method: key.slice(0, idx), name: key.slice(idx + 1) };
    };

    const flattenCatalogCategory = (catalog, category) => {
        const bucket = normalizeInputFilesBucket(catalog?.[category]);
        const entries = [];
        INPUT_FILE_METHODS.forEach((method) => {
            bucket[method].forEach((name) => {
                if (name) entries.push({ name, method, key: getFileEntryKey(method, name) });
            });
        });
        return entries;
    };

    const getSelectedKeysForCategory = (selectedInputFiles, category) => {
        const bucket = normalizeInputFilesBucket(selectedInputFiles?.[category]);
        const keys = [];
        INPUT_FILE_METHODS.forEach((method) => {
            bucket[method].forEach((name) => {
                if (name) keys.push(getFileEntryKey(method, name));
            });
        });
        return keys;
    };

    const countSelectedForCategory = (selectedInputFiles, category) =>
        getSelectedKeysForCategory(selectedInputFiles, category).length;

    const buildConfigInputFiles = (selectedInputFiles, frameFiles, advFrameEnabled) => {
        const out = getDefaultInputFiles();
        TEST_FILE_BUCKET_KEYS.forEach((key) => {
            if (key === 'frame') return;
            out[key] = normalizeInputFilesBucket(selectedInputFiles?.[key]);
        });
        if (advFrameEnabled && Array.isArray(frameFiles) && frameFiles.length > 0) {
            out.frame.uploaded = frameFiles
                .map((file) => (typeof file === 'string' ? file : file?.file_name))
                .filter(Boolean);
        }
        return out;
    };

    // UI-only pretty labels for categories and subcategories

    const [profiles, setProfiles] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [profilesLoading, setProfilesLoading] = useState(true);
    const [error, setError] = useState(null);
    const [serverUnavailable, setServerUnavailable] = useState(false);
    const [hidingParams, setHidingParams] = useState(new Set());
    const [apiResponse, setApiResponse] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('id');
    const [sortAsc, setSortAsc] = useState(false);

    // Function to fetch test files from API
    const fetchTestFiles = async () => {
        try {
            const response = await fetch(getApiUrl('/api/internal/test-files/list/'), {
                method: "GET",
                credentials: 'include', // Send session cookies
                redirect: "follow"
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            // API shape: { success: true, input_files: { prompts: { internal, uploaded, generated }, ... } }
            if (data.success && data.input_files && typeof data.input_files === 'object') {
                setCatalogInputFiles(normalizeInputFiles(data.input_files));
            } else {
                throw new Error(t('errors.invalidApiResponse'));
            }
        } catch (error) {
            console.error('Error fetching test files:', error);
            setCatalogInputFiles(getDefaultInputFiles());
        }
    };

    // Helper function to get the first allowed profile type
    const getFirstAllowedProfileType = (allowedTypes) => {
        const profileTypeOrder = ['prompts_docs_llm', 'dos_tester', 'ai_model_attack_surface', 'mcp_tester', 'rto', 'regulation_compliance'];

        // Check each profile type in order
        for (const type of profileTypeOrder) {
            if (allowedTypes[type]?.allowed !== false) {
                return type;
            }
        }

        // If none are allowed, default to the first type
        return 'prompts_docs_llm';
    };

    // Function to fetch allowed test types from API
    const fetchAllowedTestTypes = async () => {
        try {
            const response = await fetch(getApiUrl('/api/internal/license/allowedTestTypes/'), {
                method: 'GET',
                credentials: 'include', // Send session cookies
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();

            if (data.success && data.allowed_test_types) {
                setAllowedTestTypes(data.allowed_test_types);
                return data.allowed_test_types;
            } else {
                throw new Error(t('errors.invalidApiResponse'));
            }
        } catch (error) {
            console.error('Error fetching allowed test types:', error);
            // On error, allow all types by default
            const defaultTypes = {
                prompts_docs_llm: { allowed: true },
                ai_model_attack_surface: { allowed: true },
                mcp_tester: { allowed: true },
                dos_tester: { allowed: true },
                rto: { allowed: true },
                regulation_compliance: { allowed: true }
            };
            setAllowedTestTypes(defaultTypes);
            return defaultTypes;
        }
    };

    // Helper functions for profile type-based page visibility
    const getVisiblePages = () => {
        // Page 1 (Profile Type) and Page 12 (Summary) are always visible
        const alwaysVisible = [1, 12];

        switch (profileType) {
            case 'prompts_docs_llm':
                return [1, 2, 3, 4, 12]; // Profile Type, Prompts, Advanced Multi-stage/Multi-step Attacks, Document Upload, Summary
            case 'ai_model_attack_surface':
                return [1, 5, 12]; // Profile Type, LLM, Summary
            case 'mcp_tester':
                return [1, 9, 8, 12]; // Profile Type, Safe-Mode MCP Tests, Intrusive-Mode MCP Tests, Summary
            case 'dos_tester':
                return [1, 6, 7, 12]; // Profile Type, AI-specific Denial-of-Service, Advance Agentic-AI Attacks, Summary
            case 'rto':
                return [1, 10, 12]; // Profile Type, RTO, Summary
            case 'regulation_compliance':
                return [1, 11, 12]; // Profile Type, Regulation, Summary
            default:
                return alwaysVisible;
        }
    };

    const isPageVisible = (page) => {
        return getVisiblePages().includes(page);
    };

    const getNextVisiblePage = (currentPage) => {
        const visiblePages = getVisiblePages();
        const currentIndex = visiblePages.indexOf(currentPage);
        if (currentIndex >= 0 && currentIndex < visiblePages.length - 1) {
            return visiblePages[currentIndex + 1];
        }
        return currentPage; // Already at last visible page
    };

    const getPreviousVisiblePage = (currentPage) => {
        const visiblePages = getVisiblePages();
        const currentIndex = visiblePages.indexOf(currentPage);
        if (currentIndex > 0) {
            return visiblePages[currentIndex - 1];
        }
        return currentPage; // Already at first visible page
    };

    const getVisibleTabLabels = () => {
        const visiblePages = getVisiblePages();
        return tabLabels.filter((_, index) => visiblePages.includes(index + 1));
    };

    // Helper function to get categories that should be enabled for a profile type
    const getCategoriesForProfileType = (type) => {
        switch (type) {
            case 'prompts_docs_llm':
                return [
                    'prompts_testing',
                    'malicious_doc_uploads',
                    'adv_multi_attacks'
                ];
            case 'ai_model_attack_surface':
                return ['llm_api_mis_use_abuse'];
            case 'mcp_tester':
                return ['mcp_agent', 'safe_mcp_tests'];
            case 'dos_tester':
                return ['llm_dos', 'adv_agentic_ai_attacks'];
            case 'rto':
                return ['rto_tests'];
            case 'regulation_compliance':
                return ['regulation_compliance_testing'];
            default:
                return [];
        }
    };

    // Helper function to get subcategories that belong to a category
    const getSubcategoriesForCategory = (category) => {
        if (!editing?.config?.[category]?.sub) return [];
        return Object.keys(editing.config[category].sub);
    };

    // Function to update config based on profile type - disable irrelevant categories
    const updateConfigForProfileType = (newProfileType) => {
        if (!editing?.config) return;

        const enabledCategories = getCategoriesForProfileType(newProfileType);
        const allCategories = Object.keys(defaultCategoryMap);

        setEditing((prev) => {
            if (!prev) return prev;

            const updatedConfig = normalizeConfig(prev.config);

            // Process each category - only disable categories that are NOT in enabledCategories
            allCategories.forEach((cat) => {
                const isRelevantCategory = enabledCategories.includes(cat);
                const currentCategory = updatedConfig[cat];

                if (currentCategory && !isRelevantCategory) {
                    // Only disable categories that are NOT relevant to the selected profile type
                    // Don't change categories that ARE relevant - let user enable them manually
                    updatedConfig[cat] = {
                        ...currentCategory,
                        enabled: false
                    };

                    // Disable all subcategories and clear params (only for categories that have subcategories)
                    if (currentCategory.sub && typeof currentCategory.sub === 'object') {
                        const updatedSubs = {};
                        Object.keys(currentCategory.sub).forEach((sub) => {
                            const paramsStructure = getNullParams(cat, sub);
                            const hasParams = paramsStructure !== null;

                            if (hasParams) {
                                updatedSubs[sub] = { enabled: false, params: paramsStructure };
                            } else {
                                updatedSubs[sub] = { enabled: false };
                            }
                        });
                        updatedConfig[cat].sub = updatedSubs;
                    }
                }
                // If category IS relevant, leave it as-is (don't change enabled state)
            });

            return {
                ...prev,
                config: updatedConfig
            };
        });

        // Clear file selections for disabled categories
        const fileCategoriesToClear = new Set();
        allCategories.forEach((cat) => {
            if (!enabledCategories.includes(cat)) {
                if (PROMPTS_CATEGORIES.includes(cat)) fileCategoriesToClear.add('prompts');
                if (cat === 'regulation_compliance_testing') fileCategoriesToClear.add('rc_prompts');
                if (cat === 'malicious_doc_uploads') fileCategoriesToClear.add('docs');
                if (cat === 'llm_api_mis_use_abuse') fileCategoriesToClear.add('llm');
                if (cat === 'rto_tests') fileCategoriesToClear.add('rto');
            }
        });
        setSelectedInputFiles((prev) => {
            const next = normalizeInputFiles(prev);
            fileCategoriesToClear.forEach((fc) => {
                next[fc] = { internal: [], generated: [], uploaded: [] };
            });
            return next;
        });
    };

    // Active file picker categories by profile type
    const getActiveFileCategories = () => {
        if (!editing?.config) return [];
        const activeCategories = [];
        if (profileType === 'prompts_docs_llm') {
            if (editing.config.prompts_testing?.enabled === true) {
                activeCategories.push({ file_category: 'prompts', label: t('fileCategories.prompts') });
            }
            if (editing.config.malicious_doc_uploads?.enabled === true) {
                activeCategories.push({ file_category: 'docs', label: t('fileCategories.docs') });
            }
            // Advanced Multi-stage/Multi-step Attacks use decision tree test files
            if (editing.config.adv_multi_attacks?.enabled === true) {
                activeCategories.push({ file_category: 'custom_decision_tree', label: t('fileCategories.decisionTrees') });
            }
        } else if (profileType === 'ai_model_attack_surface') {
            if (hasEnabledSubcategory('llm_api_mis_use_abuse')) {
                activeCategories.push({ file_category: 'llm', label: t('fileCategories.llm') });
            }
        } else if (profileType === 'dos_tester') {
            if (hasEnabledSubcategory('llm_dos')) {
                activeCategories.push({ file_category: 'dos', label: t('fileCategories.dos') });
            }
        } else if (profileType === 'rto') {
            if (hasEnabledSubcategory('rto_tests')) {
                activeCategories.push({ file_category: 'rto', label: t('fileCategories.rto') });
            }
        } else if (profileType === 'mcp_tester') {
            if (hasEnabledSubcategory('mcp_agent')) {
                activeCategories.push({ file_category: 'mcp', label: t('fileCategories.mcp') });
            }
        } else if (profileType === 'regulation_compliance') {
            if (editing.config.regulation_compliance_testing?.enabled === true) {
                activeCategories.push({ file_category: 'rc_prompts', label: t('fileCategories.rcPrompts') });
            }
        }
        return activeCategories;
    };

    const page2CategoryKeys = ['prompts_testing'];
    const isAnyPage2CategoryEnabled = () => page2CategoryKeys.some((cat) => Boolean(editing?.config?.[cat]?.enabled));
    const hasEnabledSubcategory = (cat) => {
        const catData = editing?.config?.[cat];
        if (!catData?.sub || typeof catData.sub !== 'object') return false;
        return Object.values(catData.sub).some(subVal =>
            (typeof subVal === 'object' ? subVal.enabled : subVal)
        );
    };
    const canGoNextFromCurrentPage = () => {
        if (currentPage !== 2 || !isPageVisible(2)) return true;
        if (!isAnyPage2CategoryEnabled()) return true; // no categories selected => allow next
        return (countSelectedForCategory(selectedInputFiles, 'prompts') > 0);
    };

    // Page 4: if Malicious Doc Uploads is enabled, require at least one file selection
    const canGoNextFromPage4Docs = () => {
        if (currentPage !== 4 || !isPageVisible(4)) return true;
        if (!editing?.config?.malicious_doc_uploads?.enabled) return true;
        return (countSelectedForCategory(selectedInputFiles, 'docs') > 0);
    };

    // Page 5: if LLM API Misuse/Abuse is enabled, require at least one LLM file selection
    const canGoNextFromPage5Llm = () => {
        if (currentPage !== 5 || !isPageVisible(5)) return true;
        if (!editing?.config?.llm_api_mis_use_abuse?.enabled) return true;
        return (countSelectedForCategory(selectedInputFiles, 'llm') > 0);
    };

    // Page 6 (DoS): Next is not gated on DoS file selection; llm_dos may use defaults without picked files here.
    const canGoNextFromPage6Dos = () => {
        if (currentPage !== 6 || !isPageVisible(6)) return true;
        if (!editing?.config?.llm_dos?.enabled) return true;
        return true;
    };

    // Page 10 (RTO profile): if RTO tests are enabled, require at least one RTO file selection
    const canGoNextFromPage10Rto = () => {
        if (currentPage !== 10 || !isPageVisible(10)) return true;
        if (!editing?.config?.rto_tests?.enabled) return true;
        return (countSelectedForCategory(selectedInputFiles, 'rto') > 0);
    };

    // Page 11 (Regulation): if Regulation Compliance Testing is enabled, require at least one rc_prompts file
    const canGoNextFromPage11Regulation = () => {
        if (currentPage !== 11 || !isPageVisible(11)) return true;
        if (!editing?.config?.regulation_compliance_testing?.enabled) return true;
        return (countSelectedForCategory(selectedInputFiles, 'rc_prompts') > 0);
    };

    const canGoNext = () => {
        if (!canGoNextFromCurrentPage()) return false;
        if (!canGoNextFromPage4Docs()) return false;
        if (!canGoNextFromPage5Llm()) return false;
        if (!canGoNextFromPage6Dos()) return false;
        if (!canGoNextFromPage10Rto()) return false;
        if (!canGoNextFromPage11Regulation()) return false;
        return true;
    };

        const [currentPage, setCurrentPage] = useState(1);
    // Profile type selection
    const [profileType, setProfileType] = useState('prompts_docs_llm'); // 'prompts_docs_llm', 'ai_model_attack_surface', 'mcp_tester', 'dos_tester', 'rto', 'regulation_compliance'
    // Allowed test types from license
    const [allowedTestTypes, setAllowedTestTypes] = useState({
        prompts_docs_llm: { allowed: true },
        ai_model_attack_surface: { allowed: true },
        mcp_tester: { allowed: true },
        dos_tester: { allowed: true },
        rto: { allowed: true },
        regulation_compliance: { allowed: true }
    });
    // Internal-only file picker (single system)
    const [errorPopup, setErrorPopup] = useState({
        show: false,
        message: ""
    });
    const [statusModal, setStatusModal] = useState({
        open: false,
        type: "success",
        message: "",
    });
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [showDosPrecautions, setShowDosPrecautions] = useState(false);

    // Advance Agentic-AI Attacks (page 6)
    const frameFileInputRef = useRef(null);
    const [frameFiles, setFrameFiles] = useState([]);
    // Target configs list for profile modal dropdown (from getAllTargetConfigs API)
    const [targetConfigsList, setTargetConfigsList] = useState([]);
    const [targetConfigsLoading, setTargetConfigsLoading] = useState(false);

    // Filter target configs by profile type (only show targets matching allowed types for profileType)
    const filteredTargetConfigs = useMemo(() => {
        const allowedTypes = getAllowedTargetTypes(profileType);
        return allowedTypes.length
            ? targetConfigsList.filter((ec) => allowedTypes.includes(ec.target_type))
            : [];
    }, [profileType, targetConfigsList]);

    // Test-files catalog from /list/ and per-profile selections (saved as config.input_files)
    const [catalogInputFiles, setCatalogInputFiles] = useState(() => getDefaultInputFiles());
    const [selectedAvailableFiles, setSelectedAvailableFiles] = useState([]);
    const [selectedChosenFiles, setSelectedChosenFiles] = useState([]);
    const dualListScrollRestoreRef = useRef(null); // persists across FilePickingSection remounts

    const [selectedInputFiles, setSelectedInputFiles] = useState(() => getDefaultInputFiles());

    // Start from defaultCategoryMap, overlay anything present in saved profile config (API may be pruned).
    const mergeProfileConfigWithDefaults = (saved) => {
        const base = JSON.parse(JSON.stringify(defaultCategoryMap));
        if (!saved || typeof saved !== 'object') {
            base.input_files = getDefaultInputFiles();
            return base;
        }

        Object.keys(defaultCategoryMap).forEach((catKey) => {
            const incoming = saved[catKey];
            if (incoming === undefined || incoming === null) return;
            const def = defaultCategoryMap[catKey];
            const merged = JSON.parse(JSON.stringify(def));
            if (typeof incoming.enabled === 'boolean') merged.enabled = incoming.enabled;
            if (catKey === 'rto_tests' && incoming.average_response_time !== undefined) {
                merged.average_response_time = incoming.average_response_time;
            }
            if (def.sub && incoming.sub && typeof incoming.sub === 'object') {
                Object.keys(def.sub).forEach((subKey) => {
                    const incSub = incoming.sub[subKey];
                    if (incSub === undefined || incSub === null) return;
                    merged.sub[subKey] = {
                        ...merged.sub[subKey],
                        ...incSub
                    };
                });
            }
            base[catKey] = merged;
        });

        base.input_files = getInputFilesFromProfileConfig(saved);

        if (saved.profile_type !== undefined) {
            base.profile_type = saved.profile_type;
        }

        return base;
    };

    // Normalize config: merge profile onto defaults, then ensure params / structure invariants
    const normalizeConfig = (config) => {
        const normalized = mergeProfileConfigWithDefaults(config);

        // Helper to check if params have any non-null values
        const hasNonNullValues = (params) => {
            if (!params) return false;
            return Object.values(params).some(val => val != null && val !== '');
        };

        // Helper to convert empty strings to null in params (preserve booleans)
        const cleanParams = (params) => {
            if (!params) return params;
            const cleaned = {};
            Object.entries(params).forEach(([key, value]) => {
                // Preserve booleans, convert empty strings to null
                if (typeof value === 'boolean') {
                    cleaned[key] = value;
                } else {
                    cleaned[key] = (value === '') ? null : value;
                }
            });
            return cleaned;
        };

        // Ensure adv_agentic_ai_attacks always has full structure (matching other categories)
        if (!normalized.adv_agentic_ai_attacks || !normalized.adv_agentic_ai_attacks.sub || typeof normalized.adv_agentic_ai_attacks.sub !== 'object') {
            normalized.adv_agentic_ai_attacks = JSON.parse(JSON.stringify(defaultCategoryMap.adv_agentic_ai_attacks));
        }

        // Ensure adv_multi_attacks always has full structure
        if (!normalized.adv_multi_attacks || !normalized.adv_multi_attacks.sub || typeof normalized.adv_multi_attacks.sub !== 'object') {
            normalized.adv_multi_attacks = JSON.parse(JSON.stringify(defaultCategoryMap.adv_multi_attacks));
        }

        // Ensure safe_mcp_tests always has full structure
        if (!normalized.safe_mcp_tests || !normalized.safe_mcp_tests.sub || typeof normalized.safe_mcp_tests.sub !== 'object') {
            normalized.safe_mcp_tests = JSON.parse(JSON.stringify(defaultCategoryMap.safe_mcp_tests));
        }

        // Normalize llm_dos, mcp_agent, and adv_agentic_ai_attacks categories
        ['llm_dos', 'mcp_agent', 'adv_agentic_ai_attacks'].forEach(cat => {
            if (normalized[cat] && normalized[cat].sub) {
                // For adv_agentic_ai_attacks, ensure all subcategories from defaultCategoryMap are present
                if (cat === 'adv_agentic_ai_attacks') {
                    const defaultSubs = defaultCategoryMap.adv_agentic_ai_attacks.sub;
                    Object.keys(defaultSubs).forEach(subKey => {
                        if (!normalized[cat].sub[subKey]) {
                            normalized[cat].sub[subKey] = JSON.parse(JSON.stringify(defaultSubs[subKey]));
                        }
                    });
                }

                Object.keys(normalized[cat].sub).forEach(sub => {
                    const subValue = normalized[cat].sub[sub];
                    if (typeof subValue === 'object' && typeof subValue.enabled === 'boolean') {
                        const defaultParams = getDefaultParams(cat, sub);
                        const nullParams = getNullParams(cat, sub);

                        if (defaultParams) {
                            // This subcategory has params
                            if (subValue.enabled) {
                                // Clean empty strings to null first
                                if (subValue.params) {
                                    normalized[cat].sub[sub].params = cleanParams(subValue.params);
                                }
                                // Enabled: merge existing params with defaults to ensure all required keys are present
                                if (!normalized[cat].sub[sub].params || !hasNonNullValues(normalized[cat].sub[sub].params)) {
                                    normalized[cat].sub[sub].params = defaultParams;
                                } else {
                                    // Merge: use existing values, but add missing keys from defaults
                                    normalized[cat].sub[sub].params = {
                                        ...defaultParams,
                                        ...normalized[cat].sub[sub].params
                                    };
                                }
                            } else {
                                // Disabled: use null values for all categories with params
                                normalized[cat].sub[sub].params = nullParams;
                            }
                        }
                        // Subcategories without params are left as-is
                    }
                });
            }
        });

        // Ensure rto_tests always has average_response_time
        if (normalized.rto_tests && typeof normalized.rto_tests === 'object') {
            if (!normalized.rto_tests.hasOwnProperty('average_response_time')) {
                normalized.rto_tests.average_response_time = 'overall';
            }
        }

        return normalized;
    };

    // ----- Toggle Handlers -----
    const toggleCategory = (cat) => {
        setEditing((prev) => {
            if (!prev) return prev;
            const enabled = !prev.config[cat].enabled;
            const currentCategory = prev.config[cat];
            if (!currentCategory?.sub || typeof currentCategory.sub !== 'object') {
                return {
                    ...prev,
                    config: {
                        ...prev.config,
                        [cat]: { ...currentCategory, enabled },
                    },
                };
            }
            const newSubs = Object.fromEntries(
                Object.keys(currentCategory.sub).map((s) => {
                    const paramsStructure = getNullParams(cat, s);
                    const hasParams = paramsStructure !== null;
                    const currentSub = currentCategory.sub[s];

                    // Initialize params for llm_dos and mcp_agent subcategories
                    if ((cat === 'llm_dos' || cat === 'mcp_agent') && hasParams) {
                        const existingParams = typeof currentSub === 'object' && currentSub.params
                            ? currentSub.params
                            : null;

                        if (enabled) {
                            // Helper to check if params have any non-null values
                            const hasNonNullValues = (params) => {
                                if (!params) return false;
                                return Object.values(params).some(val => val != null);
                            };

                            if (cat === 'llm_dos') {
                                if (s === 'resource_exhaustion_dos') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { concurrent_requests: 200, test_intensity: 'normal' } }];
                                } else if (s === 'cost_amplification') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_cost_requests: 50 } }];
                                } else if (s === 'service_disruption') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { sustained_duration: 300, sustained_rps: 20 } }];
                                } else if (s === 'memory_exhaustion') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { payload_size: 20, number_of_memory_requests: 30 } }];
                                } else if (s === 'token_overflow') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { max_context_length: 128000 } }];
                                } else if (s === 'rate_limit_bypass') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { test_strategy: 'parameter_mutation' } }];
                                } else if (s === 'test_context_overflow') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { phases: 10 } }];
                                } else if (s === 'test_prompt_injection_dos') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { injection_type: 'direct' } }];
                                } else if (s === 'test_repetitive_requests') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { pattern_type: 'identical' } }];
                                } else if (s === 'test_session_hijacking_dos') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { sessions_per_ip: 5 } }];
                                } else if (s === 'test_burst_attacks') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { burst_size: 50, interval: 2 } }];
                                } else if (s === 'test_slowloris_ai') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { chunks: 5, delay: 2 } }];
                                } else if (s === 'test_tool_execution_abuse') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { tool_calls: 20 } }];
                                } else if (s === 'test_model_poisoning_dos') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { poisoning_attempts: 10 } }];
                                } else if (existingParams) {
                                    return [s, { enabled, params: existingParams }];
                                }
                            } else if (cat === 'mcp_agent') {
                                if (s === 'resource_exhaustion_mcpa') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { concurrent_resource_requests: 160 } }];
                                } else if (s === 'protocol_violation') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_protocol_requests: 60 } }];
                                } else if (s === 'tool_abuse') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_tool_requests: 200 } }];
                                } else if (s === 'memory_leak') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_iterations: 20 } }];
                                } else if (s === 'session_hijack') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_sessions: 40 } }];
                                } else if (s === 'context_overflow') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { context_overflow_count: 0 } }];
                                } else if (s === 'malicious_tool_calls') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_malicious_requests: 100 } }];
                                } else if (s === 'test_path_traversal') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { path_trav_attacks: 20 } }];
                                } else if (s === 'test_command_injection') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { cmd_inj_attacks: 20 } }];
                                } else if (s === 'test_sql_injection') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { sql_inj_attacks: 20 } }];
                                } else if (s === 'test_data_exfiltration') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { data_exfil_attacks: 20 } }];
                                } else if (s === 'test_prompt_injection_mcpa') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { prompt_inj_attacks: 20 } }];
                                } else if (s === 'test_auth_bypass') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { auth_bypass_attacks: 20 } }];
                                } else if (s === 'test_malicious_content') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { mal_cont_attacks: 20 } }];
                                } else if (s === 'test_session_hijacking_mcpa') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { session_hijack_attacks: 20 } }];
                                } else if (s === 'test_tool_misuse') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { tool_misuse_attacks: 20 } }];
                                } else if (s === 'test_generic_injection') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { gen_inj_attacks: 20 } }];
                                } else if (s === 'test_resource_exhaustion') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { rsc_ex_attacks: 10 } }];
                                } else if (s === 'test_benign_requests') {
                                    return [s, { enabled, params: (existingParams && hasNonNullValues(existingParams)) ? existingParams : { benign_reqs_num: 20 } }];
                                } else if (existingParams) {
                                    return [s, { enabled, params: existingParams }];
                                }
                            }
                        } else {
                            // When disabling, set params to null structure
                            return [s, { enabled, params: paramsStructure }];
                        }
                    }
                    // For subcategories without params, don't add params
                    // Preserve existing params if they exist for other cases
                    if (typeof currentSub === 'object' && currentSub.params) {
                        return [s, { enabled, params: currentSub.params }];
                    }
                    return [s, { enabled }];
                })
            );
            // Preserve average_response_time for rto_tests (always present)
            const newCategoryValue = cat === 'rto_tests'
                ? { enabled, sub: newSubs, average_response_time: prev.config[cat]?.average_response_time || 'overall' }
                : { enabled, sub: newSubs };

            return {
                ...prev,
                config: {
                    ...prev.config,
                    [cat]: { enabled, sub: newSubs },
                },
            };
        });
        // If the entire category is being disabled, clear selected files for its file_category
        let fileCategory = null;
        if (PROMPTS_CATEGORIES.includes(cat)) fileCategory = 'prompts';
        else if (cat === 'regulation_compliance_testing') fileCategory = 'rc_prompts';
        else if (cat === 'malicious_doc_uploads') fileCategory = 'docs';
        else if (cat === 'llm_api_mis_use_abuse') fileCategory = 'llm';
        else if (cat === 'rto_tests') fileCategory = 'rto';
        if (fileCategory) {
            const categoryCurrentlyEnabled = editing?.config?.[cat]?.enabled;
            const willDisable = categoryCurrentlyEnabled === true;
            if (willDisable) {
                setSelectedInputFiles((prev) => {
                    const next = normalizeInputFiles(prev);
                    next[fileCategory] = { internal: [], generated: [], uploaded: [] };
                    return next;
                });
            }
        }
    };

    const toggleSub = (cat, sub) => {
        setEditing((prev) => {
            if (!prev) return prev;
            const currentSubValue = prev.config[cat].sub[sub];
            const currentEnabled = typeof currentSubValue === 'object' ? currentSubValue.enabled : currentSubValue;
            const willEnable = !currentEnabled;

            // If disabling and this subcategory has params, trigger hide animation
            if (!willEnable && (cat === 'llm_dos' || cat === 'mcp_agent' || cat === 'adv_agentic_ai_attacks')) {
                const key = `${cat}-${sub}`;
                setHidingParams(prev => new Set(prev).add(key));
                // Remove from hiding set and actually disable after animation
                setTimeout(() => {
                    setHidingParams(prev => {
                        const next = new Set(prev);
                        next.delete(key);
                        return next;
                    });
                }, 400);
            } else {
                // If enabling, remove from hiding set immediately
                const key = `${cat}-${sub}`;
                setHidingParams(prev => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
            }

            // Initialize parameters for llm_dos, mcp_agent, and adv_agentic_ai_attacks subcategories
            let newSubValue = { enabled: willEnable };
            const paramsStructure = getNullParams(cat, sub);
            const hasParams = paramsStructure !== null;

            if (hasParams) {
                if (willEnable) {
                    // Check if params already exist with non-null values to preserve them
                    const existingParams = typeof currentSubValue === 'object' && currentSubValue.params
                        ? currentSubValue.params
                        : null;

                    // Helper to check if params have any non-null values
                    const hasNonNullValues = (params) => {
                        if (!params) return false;
                        return Object.values(params).some(val => val != null);
                    };

                    if (cat === 'llm_dos') {
                        if (sub === 'resource_exhaustion_dos') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { concurrent_requests: 200, test_intensity: 'normal' };
                        } else if (sub === 'cost_amplification') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_cost_requests: 50 };
                        } else if (sub === 'service_disruption') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { sustained_duration: 300, sustained_rps: 20 };
                        } else if (sub === 'memory_exhaustion') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { payload_size: 20, number_of_memory_requests: 30 };
                        } else if (sub === 'token_overflow') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { max_context_length: 128000 };
                        } else if (sub === 'rate_limit_bypass') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { test_strategy: 'parameter_mutation' };
                        } else if (sub === 'test_context_overflow') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { phases: 10 };
                        } else if (sub === 'test_prompt_injection_dos') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { injection_type: 'direct' };
                        } else if (sub === 'test_repetitive_requests') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { pattern_type: 'identical' };
                        } else if (sub === 'test_session_hijacking_dos') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { sessions_per_ip: 5 };
                        } else if (sub === 'test_burst_attacks') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { burst_size: 50, interval: 2 };
                        } else if (sub === 'test_slowloris_ai') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { chunks: 5, delay: 2 };
                        } else if (sub === 'test_tool_execution_abuse') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { tool_calls: 20 };
                        } else if (sub === 'test_model_poisoning_dos') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { poisoning_attempts: 10 };
                        }
                    } else if (cat === 'mcp_agent') {
                        if (sub === 'resource_exhaustion_mcpa') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { concurrent_resource_requests: 160 };
                        } else if (sub === 'protocol_violation') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_protocol_requests: 60 };
                        } else if (sub === 'tool_abuse') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_tool_requests: 200 };
                        } else if (sub === 'memory_leak') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_iterations: 20 };
                        } else if (sub === 'session_hijack') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_sessions: 40 };
                        } else if (sub === 'context_overflow') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { context_overflow_count: 0 };
                        } else if (sub === 'malicious_tool_calls') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { number_of_malicious_requests: 100 };
                        } else if (sub === 'test_path_traversal') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { path_trav_attacks: 20 };
                        } else if (sub === 'test_command_injection') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { cmd_inj_attacks: 20 };
                        } else if (sub === 'test_sql_injection') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { sql_inj_attacks: 20 };
                        } else if (sub === 'test_data_exfiltration') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { data_exfil_attacks: 20 };
                        } else if (sub === 'test_prompt_injection_mcpa') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { prompt_inj_attacks: 20 };
                        } else if (sub === 'test_auth_bypass') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { auth_bypass_attacks: 20 };
                        } else if (sub === 'test_malicious_content') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { mal_cont_attacks: 20 };
                        } else if (sub === 'test_session_hijacking_mcpa') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { session_hijack_attacks: 20 };
                        } else if (sub === 'test_tool_misuse') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { tool_misuse_attacks: 20 };
                        } else if (sub === 'test_generic_injection') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { gen_inj_attacks: 20 };
                        } else if (sub === 'test_resource_exhaustion') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { rsc_ex_attacks: 10 };
                        } else if (sub === 'test_benign_requests') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams)) ? existingParams : { benign_reqs_num: 20 };
                        }
                    } else if (cat === 'adv_agentic_ai_attacks') {
                        if (sub === 'adv_crescendo') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams))
                                ? existingParams
                                : { crescendo_prompt: '', stages: 0, requests_per_stage: 0, intensity_multiplier: 1.0 };
                        } else if (sub === 'adv_trolley') {
                            newSubValue.params = (existingParams && hasNonNullValues(existingParams))
                                ? existingParams
                                : { trolley_prompt: '', num_dilemmas: 0, recursion_depth: 0, complexity_level: 'high' };
                        } else if (sub === 'adv_converter') {
                            // When enabling adv_converter, always set all converter params to true (ignore previous state)
                            newSubValue.params = {
                                base64_conv: true,
                                single_char_conv: true,
                                repeat_conv: true,
                                caesar_conv: true,
                                leet_conv: true,
                                unicode_conv: true
                            };
                        }
                    }
                } else {
                    // When disabling, set params to null structure
                    newSubValue.params = paramsStructure;
                }
            }

            const newSubs = {
                ...prev.config[cat].sub,
                [sub]: newSubValue,
            };
            const parentOn = Object.values(newSubs).some(subVal =>
                typeof subVal === 'object' ? subVal.enabled : subVal
            );
            return {
                ...prev,
                config: {
                    ...prev.config,
                    [cat]: { enabled: parentOn, sub: newSubs },
                },
            };
        });
        // Note: We no longer clear file_category selections when disabling a single subcategory,
        // since file_category buckets are shared across subs. Only category-level disable clears.
    };

    // Update subcategory parameters
    const updateSubParams = (cat, sub, paramKey, paramValue) => {
        setEditing((prev) => {
            if (!prev) return prev;
            const currentSubValue = prev.config[cat].sub[sub];
            if (typeof currentSubValue !== 'object' || !currentSubValue.params) {
                return prev;
            }
            const newParams = {
                ...currentSubValue.params,
                [paramKey]: paramValue
            };
            let newSubValue = {
                ...currentSubValue,
                params: newParams
            };
            // When all converter params are unchecked, turn off adv_converter main switch
            if (cat === 'adv_agentic_ai_attacks' && sub === 'adv_converter') {
                const converterParamKeys = ['base64_conv', 'single_char_conv', 'repeat_conv', 'caesar_conv', 'leet_conv', 'unicode_conv'];
                const allOff = converterParamKeys.every((k) => newParams[k] === false);
                if (allOff) {
                    newSubValue = { ...newSubValue, enabled: false };
                }
            }
            const updatedSubs = {
                ...prev.config[cat].sub,
                [sub]: newSubValue
            };
            const parentOn = Object.values(updatedSubs).some(subVal =>
                typeof subVal === 'object' ? subVal.enabled : subVal
            );
            return {
                ...prev,
                config: {
                    ...prev.config,
                    [cat]: {
                        ...prev.config[cat],
                        enabled: parentOn,
                        sub: updatedSubs
                    }
                },
            };
        });
    };

    // Helper function to handle number input validation and update params
    const handleNumberParamChange = (cat, sub, paramKey, inputValue, min = 0, max = null) => {
        // Allow empty string for clearing (store as empty string, not null, so user can clear field)
        if (inputValue === '') {
            updateSubParams(cat, sub, paramKey, '');
            return;
        }
        const numValue = parseInt(inputValue);
        // Allow any valid number during typing (don't block intermediate values)
        // This allows users to type values like "6" to eventually get to "600"
        if (!isNaN(numValue)) {
            updateSubParams(cat, sub, paramKey, numValue);
        }
    };

    // Helper function to clamp value to min/max range on blur
    const handleNumberParamBlur = (cat, sub, paramKey, inputValue, min = 0, max = null) => {
        if (inputValue === '' || inputValue === null || inputValue === undefined) {
            // If empty, set to minimum value
            updateSubParams(cat, sub, paramKey, min);
            return;
        }
        const numValue = parseInt(inputValue);
        if (isNaN(numValue)) {
            // If invalid, set to minimum
            updateSubParams(cat, sub, paramKey, min);
            return;
        }
        // Clamp to min/max range
        let clampedValue = numValue;
        if (clampedValue < min) {
            clampedValue = min;
        }
        if (max !== null && clampedValue > max) {
            clampedValue = max;
        }
        updateSubParams(cat, sub, paramKey, clampedValue);
    };

    // Build adv_agentic_ai_attacks config from editing.config for profile JSON.
    // Params are derived from getDefaultParams (same model as other categories),
    // not from defaultCategoryMap sub entries.
    const buildAdvAgenticAiAttacksConfig = () => {
        const defaults = defaultCategoryMap.adv_agentic_ai_attacks;
        const defaultSub = defaults.sub;
        if (!editing?.config?.adv_agentic_ai_attacks) {
            return JSON.parse(JSON.stringify(defaults));
        }
        const adv = editing.config.adv_agentic_ai_attacks;
        if (!adv.sub || typeof adv.sub !== 'object') {
            return JSON.parse(JSON.stringify(defaults));
        }
        const anyEnabled = Object.values(adv.sub).some(subVal =>
            typeof subVal === 'object' && subVal.enabled === true
        );
        // Build sub so every key has full structure with params (merge with defaults)
        const sub = {};
        Object.keys(defaultSub).forEach((subKey) => {
            const defaultEntry = defaultSub[subKey];
            const fromState = adv.sub[subKey];
            const defaultParams = getDefaultParams('adv_agentic_ai_attacks', subKey);
            if (typeof fromState === 'object' && typeof fromState.enabled === 'boolean') {
                if (defaultParams) {
                    sub[subKey] = {
                        enabled: fromState.enabled,
                        params: fromState.params != null
                            ? { ...defaultParams, ...fromState.params }
                            : { ...defaultParams }
                    };
                } else {
                    sub[subKey] = { enabled: fromState.enabled };
                }
            } else {
                sub[subKey] = JSON.parse(JSON.stringify(defaultEntry));
            }
        });
        return { enabled: anyEnabled, sub };
    };

    // Structural checks only for categories relevant to this profile type (matches visible pages).
    const validateConfigForProfileType = (config, profileType) => {
        if (!config || typeof config !== 'object') return false;
        const requiredCategories = getCategoriesForProfileType(profileType);
        for (const category of requiredCategories) {
            if (!config[category] || typeof config[category].enabled !== 'boolean') {
                return false;
            }
            if (category !== 'malicious_doc_uploads' && category !== 'prompts_testing' &&
                category !== 'regulation_compliance_testing' &&
                (!config[category].sub || typeof config[category].sub !== 'object')) {
                return false;
            }
        }
        return true;
    };

    // Final JSON: only category blocks for the selected profile type; input_files kept in full (all buckets, empty arrays where unused).
    const pruneConfigForProfileType = (config, profileType) => {
        const allowed = new Set(getCategoriesForProfileType(profileType));
        const out = {};
        allowed.forEach((key) => {
            if (config[key] !== undefined && config[key] !== null) {
                out[key] = JSON.parse(JSON.stringify(config[key]));
            }
        });
        if (config.input_files !== undefined) {
            out.input_files = JSON.parse(JSON.stringify(config.input_files));
        }
        if (config.profile_type !== undefined) {
            out.profile_type = config.profile_type;
        }
        return out;
    };

    const saveProfile = async () => {
        setLoading(true);

        try {
            // Basic validation
            if (!editing.profile_name || editing.profile_name.trim() === '') {
                throw new Error(t("validation.profileNameRequired"));
            }

            // Validate profile name format
            const nameValidation = validateProfileNameOrDescription(editing.profile_name);
            if (!nameValidation.isValid) {
                throw new Error(t(nameValidation.errorKey, { field: t("fields.profileName") }));
            }

            // Validate profile name length
            if (editing.profile_name && editing.profile_name.length > 50) {
                throw new Error(t("validation.profileNameMaxLength"));
            }

            // Validate profile description format
            if (editing.profile_description && editing.profile_description.trim() !== '') {
                const descValidation = validateProfileNameOrDescription(editing.profile_description);
                if (!descValidation.isValid) {
                    throw new Error(t(descValidation.errorKey, { field: t("fields.profileDescription") }));
                }
            }

            // Validate profile description length
            if (editing.profile_description && editing.profile_description.length > 500) {
                throw new Error(t("validation.profileDescriptionMaxLength"));
            }

            // Target selection is mandatory
            const selectedId = (editing.selected_target ?? '').toString().trim();
            if (!selectedId) {
                throw new Error(t("validation.selectTarget"));
            }
            const allowedTargetTypes = getAllowedTargetTypes(profileType);
            const isValidTarget = allowedTargetTypes.length > 0 && targetConfigsList.some(
                (ec) => allowedTargetTypes.includes(ec.target_type) && String(ec.id) === selectedId
            );
            if (!isValidTarget) {
                throw new Error(t("validation.selectTarget"));
            }

            // At least one category must be enabled
            const enabledCategories = Object.entries(editing.config || {}).filter(([_, catData]) =>
                catData && typeof catData === 'object' && catData.enabled === true
            );
            const hasAdvMultiAttacksFiles =
                frameFiles.length > 0 ||
                countSelectedForCategory(selectedInputFiles, 'custom_decision_tree') > 0;
            if (enabledCategories.length === 0 && !hasAdvMultiAttacksFiles) {
                throw new Error(t("validation.noCategoriesEnabled"));
            }

            // FRAME: when adv_frame is enabled, require at least one uploaded file (via test-files/upload/) on create
            const advFrameEnabled = editing?.config?.adv_agentic_ai_attacks?.sub?.adv_frame?.enabled === true;
            if (advFrameEnabled) {
                const isCreate = !editing.id || editing.isNew;
                if (isCreate && frameFiles.length === 0) {
                    throw new Error(t("validation.frameFileRequired"));
                }
            }

            const retryAttempts = FIXED_RETRY_ATTEMPTS;

            const includeTesterThrottle = profileType !== 'dos_tester';
            const toThrottleConfig = (setting, min, max, errorKey) => {
                const autoSelect = setting?.auto_select !== false;
                if (autoSelect) return { auto_select: true };
                const value = Number(setting?.value);
                if (!Number.isInteger(value) || value < min || value > max) {
                    throw new Error(t(errorKey));
                }
                return { auto_select: false, value };
            };
            const concurrentTesting = includeTesterThrottle
                ? toThrottleConfig(editing.concurrent_testing, 1, CONCURRENT_TESTING_MAX, "validation.concurrentTestingRange")
                : null;
            const promptsPerSec = includeTesterThrottle
                ? toThrottleConfig(editing.prompts_per_sec, 1, PROMPTS_PER_SEC_MAX, "validation.promptsPerSecRange")
                : null;

            // Create FormData - exactly matching backend expectations
            const formData = new FormData();

            // Required fields (matching backend)
            // user_id is obtained from session on backend, not sent in request
            formData.append('profile_name', editing.profile_name.trim());
            formData.append('profile_description', editing.profile_description || '');
            formData.append('retry_attempts', retryAttempts);

            // Config (ensure valid structure) and include internal file selections
            // Normalize config to ensure enabled subcategories have defaults for null params
            let configToSend = normalizeConfig({ ...editing.config });

            const advFrameEnabledInConfig =
                configToSend?.adv_agentic_ai_attacks?.sub?.adv_frame?.enabled === true;
            const inputFiles = buildConfigInputFiles(selectedInputFiles, frameFiles, advFrameEnabledInConfig);
            configToSend.input_files = inputFiles;
            configToSend.profile_type = profileType; // Add the profile type to config
            // Advance Agentic-AI Attacks: build adv_agentic_ai_attacks for profile JSON
            const advAgentic = buildAdvAgenticAiAttacksConfig();
            configToSend.adv_agentic_ai_attacks = advAgentic;
            if (!validateConfigForProfileType(configToSend, profileType)) {
                const base = JSON.parse(JSON.stringify(defaultCategoryMap));
                base.input_files = inputFiles;
                configToSend = base;
                configToSend.adv_agentic_ai_attacks = advAgentic;
                configToSend.profile_type = profileType;
                if (!validateConfigForProfileType(configToSend, profileType)) {
                    throw new Error(t('validation.invalidConfig'));
                }
            }
            configToSend = pruneConfigForProfileType(configToSend, profileType);
            if (includeTesterThrottle) {
                configToSend.concurrent_testing = concurrentTesting;
                configToSend.prompts_per_sec = promptsPerSec;
            }
            formData.append('config', JSON.stringify(configToSend));
            formData.append('profile_type', profileType || 'prompts_docs_llm');
            formData.append('target_config_id', editing.selected_target ?? '');

            // FRAME input file is uploaded via /api/internal/test-files/upload/ and tracked in config.input_files.frame.uploaded

            // Get CSRF token for session-based authentication
            const csrfToken = await getCsrfToken();
            // Removed: user_id is now obtained from session

            // Determine request URL and method
            const isUpdate = editing.id && !editing.isNew;
            const requestUrl = isUpdate
                ? getApiUrl(`/api/internal/profiles-config/${editing.id}/update/`)
                : getApiUrl("/api/internal/profiles-config/create/");

            // Use POST for file uploads (Django handles multipart better with POST)
            const method = 'POST';

            const response = await fetch(requestUrl, {
                method: method,
                credentials: 'include', // Send session cookies
                headers: {
                    'X-CSRFToken': csrfToken
                },
                body: formData
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.success === true) {
                // Re-fetch profiles from API instead of updating local state
                await fetchProfiles();

                // Clear form and close modal immediately
                setEditing(null);

            } else {
                throw new Error(data.detail || t('errors.failedToSave'));
            }

        } catch (err) {
            console.error('Save Profile Error:', err);
            setErrorPopup({ show: true, message: t('errors.saveFailed', { message: err.message }) });
        } finally {
            setLoading(false);
        }
    };

    const handleFinishProfile = () => {
        // Reuse existing saveProfile validation, including target checks
        saveProfile();
    };

    const deleteProfile = async (id) => {
        setLoading(true);
        try {
            // Get CSRF token for session-based authentication
            const csrfToken = await getCsrfToken();

            const response = await fetch(getApiUrl(`/api/internal/profiles-config/${id}/delete/`), {
                method: 'DELETE',
                credentials: 'include', // Send session cookies
                headers: {
                    'X-CSRFToken': csrfToken
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ detail: response.statusText }));
                throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
            }

            await response.json();
            // Remove profile from local state only on success
            setProfiles((prev) => prev.filter((p) => p.id !== id));
            setStatusModal({
                open: true,
                type: "success",
                message: t("errors.deleteSuccess"),
            });
        } catch (err) {
            console.error('Error deleting profile:', err);
            setStatusModal({
                open: true,
                type: "error",
                message: t("errors.failedToDelete", { message: err.message }),
            });
            // Don't remove from local state if deletion failed
        } finally {
            setLoading(false);
        }
    };

    const openNewProfile = async () => {
        // Fetch allowed test types first, then determine which profile type to pre-select
        const fetchedAllowedTypes = await fetchAllowedTestTypes();
        const allowedTypesToUse = fetchedAllowedTypes || allowedTestTypes;
        const selectedProfileType = getFirstAllowedProfileType(allowedTypesToUse);
        setEditing({
            id: null,
            profile_name: t("newProfileDefault"),
            users: 0,
            profile_description: "",
            selected_target: "",
            generatedOn: "",
            config: JSON.parse(JSON.stringify(defaultCategoryMap)), // Deep clone to avoid mutations
            retry_attempts: FIXED_RETRY_ATTEMPTS,
            concurrent_testing: { auto_select: true, value: 1 },
            prompts_per_sec: { auto_select: true, value: 1 },
            isNew: true,
        });
        setCurrentPage(1);
        setProfileType(selectedProfileType); // Pre-select first allowed profile type
        // Reset internal files picker
        setSelectedAvailableFiles([]);
        setSelectedChosenFiles([]);
        setSelectedInputFiles(getDefaultInputFiles());
        setFrameFiles([]);
        // Advance Agentic-AI Attacks state is now part of editing.config, reset handled by defaultCategoryMap
        // Fetch test files from API
        fetchTestFiles();
    };

    // Helper to get enabled state for agentic subcategory from config
    const getAgenticEnabled = (subKey) => {
        if (!editing?.config?.adv_agentic_ai_attacks?.sub) return false;
        const sub = editing.config.adv_agentic_ai_attacks.sub[subKey];
        return typeof sub === 'object' ? sub.enabled : false;
    };

    const toggleAgenticSelection = (key) => {
        if (!key) return;
        toggleSub('adv_agentic_ai_attacks', key);
    };

    const retryFetchProfiles = () => {
        setServerUnavailable(false);
        fetchProfiles();
    };

    async function fetchProfiles() {
        setProfilesLoading(true);
        try {
            // Removed: user_id query parameter - user is obtained from session

            // Add timeout to prevent hanging
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            const res = await fetch(getApiUrl('/api/internal/profiles-config/getAllProfiles/'), {
                method: "GET",
                credentials: 'include', // Send session cookies
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || `HTTP ${res.status}`);
            }
            const data = await res.json();
            setProfiles(data);
            setServerUnavailable(false); // Reset server status on success
        } catch (err) {
            // Check if it's a network/server error or timeout
            let errorMessage;
            let isServerError = false;

            if (err.name === 'AbortError') {
                errorMessage = t("errors.requestTimedOut");
                isServerError = true;
            } else if (err.name === 'TypeError' || err.message.includes('Failed to fetch') || err.message.includes('NetworkError')) {
                errorMessage = t("errors.serverUnavailable");
                isServerError = true;
            } else if (err.message.includes('User ID not found')) {
                errorMessage = err.message;
            } else {
                errorMessage = err.message || t("unableToLoadProfiles");
            }

            setServerUnavailable(isServerError);
            setError(err.message);
            setErrorPopup({ show: true, message: errorMessage });
        } finally {
            setProfilesLoading(false);
        }
    }


    // ---------- useEffect hooks ----------
    useEffect(() => {
        fetchProfiles();
        fetchAllowedTestTypes();
    }, []);

    // When profile type is changed to one with no file picker (e.g. dos_tester, mcp_tester), clear selected internal files so they are not sent on save
    useEffect(() => {
        if (!editing) return;
        const activeCategories = getActiveFileCategories();
        if (activeCategories.length === 0) {
            setSelectedInputFiles(getDefaultInputFiles());
        }
    }, [profileType, editing]);


    // Navigate to first visible page if current page becomes invisible
    useEffect(() => {
        if (editing && !isPageVisible(currentPage)) {
            const visiblePages = getVisiblePages();
            if (visiblePages.length > 0) {
                setCurrentPage(visiblePages[0]);
            }
        }
    }, [profileType, editing]);

    // Fetch target configs when profile create/edit modal opens (for Target dropdown).
    // Dependency is modal open state only (!!editing) so we don't refetch on every form change (e.g. selecting a target).
    const isProfileModalOpen = !!editing;
    useEffect(() => {
        if (!isProfileModalOpen) {
            setTargetConfigsList([]);
            setTargetConfigsLoading(false);
            return;
        }
        let cancelled = false;
        setTargetConfigsLoading(true);
        (async () => {
            try {
                const response = await fetch(getApiUrl("/api/internal/target-config/getAllTargetConfigs/"), {
                    method: "GET",
                    credentials: "include",
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(errorData.detail || errorData.error || t("errors.failedToLoadTargets"));
                }
                const data = await response.json();
                if (cancelled) return;
                const list = Array.isArray(data) ? data : (data.data ?? data.results ?? []);
                setTargetConfigsList(
                    list.map((item) => ({
                        id: item.id,
                        name: item.name ?? "",
                        target_type: item.target_type ?? "",
                        target_params: item.target_params ?? {},
                    }))
                );
            } catch (err) {
                if (!cancelled) {
                    console.error("Error loading Target Configs:", err);
                    setTargetConfigsList([]);
                }
            } finally {
                if (!cancelled) setTargetConfigsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isProfileModalOpen]);

    // ---------- JSX ----------
    const FilePickingSection = ({ fileCategory }) => {
        const availableListRef = useRef(null);
        const chosenListRef = useRef(null);

        useLayoutEffect(() => {
            const pending = dualListScrollRestoreRef.current;
            if (pending?.scrollTop != null && pending?.listId && pending?.fileCategory === fileCategory) {
                const listEl = pending.listId === 'available' ? availableListRef.current : chosenListRef.current;
                if (listEl) listEl.scrollTop = pending.scrollTop;
                dualListScrollRestoreRef.current = null;
            }
        });

        return (
            <div className="pm-tab2-content">
                <div style={{ gridColumn: '1 / -1' }}>
                    <div className="pm-files-section">
                        {/* Scoped internal files picker (per fileCategory) */}
                        {(() => {
                            const activeCategories = getActiveFileCategories();
                            const activeCategoryKeys = activeCategories.map((c) => c.file_category);
                            const activeCategory = (fileCategory ?? '').toString().trim();

                            if (!activeCategory || !activeCategoryKeys.includes(activeCategory)) {
                                return (
                                    <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af', paddingBottom: '20px' }}>
                                        <div style={{ fontSize: '16px', marginBottom: '8px' }}>{t('filePicker.noSelectionNeeded')}</div>
                                    </div>
                                );
                            }

                            const catalogEntriesForCategory = flattenCatalogCategory(catalogInputFiles, activeCategory);
                            const selectedKeysForCategory = getSelectedKeysForCategory(selectedInputFiles, activeCategory);

                            const availableFilesForCategory = catalogEntriesForCategory.filter(
                                (entry) => !selectedKeysForCategory.includes(entry.key)
                            );
                            const selectedFilesForCategory = selectedKeysForCategory;

                            const updateSelection = (key, add) => {
                                const { method, name } = parseFileEntryKey(key);
                                if (!name) return;
                                setSelectedInputFiles((prev) => {
                                    const next = normalizeInputFiles(prev);
                                    const bucket = normalizeInputFilesBucket(next[activeCategory]);
                                    const list = [...bucket[method]];
                                    if (add) {
                                        if (!list.includes(name)) bucket[method] = [...list, name];
                                    } else {
                                        bucket[method] = list.filter((n) => n !== name);
                                    }
                                    return { ...next, [activeCategory]: bucket };
                                });
                            };

                            return (
                                <div className="pm-dual-list-picker">
                                    <div
                                        style={{
                                            marginTop: '16px',
                                            marginBottom: '6px',
                                            fontWeight: 700,
                                            fontSize: '18px',
                                            textAlign: 'center',
                                            color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff'
                                        }}
                                    >
                                        {t('filePicker.testFiles')}
                                    </div>
                                    <div
                                        style={{
                                            marginBottom: '16px',
                                            fontSize: '13px',
                                            textAlign: 'center',
                                            color: getCurrentTheme() === 'light' ? '#4b5563' : '#9ca3af',
                                            maxWidth: '720px',
                                            marginLeft: 'auto',
                                            marginRight: 'auto',
                                        }}
                                    >
                                        {t('filePicker.intro')}
                                    </div>

                                    <div className="pm-internal-picker" style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%' }}>
                                        {/* Available files */}
                                        <div className="pm-list-box" style={{ flex: '1 1 0%', minWidth: 0, minHeight: '300px' }}>
                                            <div className="pm-list-title" style={{
                                                padding: '12px 16px',
                                                backgroundColor: getCurrentTheme() === 'light' ? '#f3f4f6' : '#374151',
                                                borderTopLeftRadius: '8px',
                                                borderTopRightRadius: '8px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                borderBottom: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #4b5563'
                                            }}>
                                                {t('filePicker.available', { count: availableFilesForCategory.length })}
                                            </div>
                                            <div ref={availableListRef} className="pm-list" role="list" style={{
                                                width: '100%',
                                                minWidth: 0,
                                                maxHeight: '250px',
                                                overflowX: 'auto',
                                                overflowY: 'auto',
                                                backgroundColor: getCurrentTheme() === 'light' ? '#fafafa' : '#111827',
                                                borderBottomLeftRadius: '8px',
                                                borderBottomRightRadius: '8px',
                                                border: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #374151',
                                                borderTop: 'none',
                                                scrollbarWidth: 'thin',
                                                scrollbarColor: getCurrentTheme() === 'light' ? '#d1d5db #fafafa' : '#374151 #111827'
                                            }}>
                                                <ul style={{ display: 'flex', flexDirection: 'column', minWidth: '100%', width: 'max-content', listStyle: 'none', padding: 0, margin: 0 }}>
                                                    {availableFilesForCategory.map((entry) => {
                                                        const id = entry.key;
                                                        const displayName = entry.name || t('filePicker.unknownFile')
                                                        return (
                                                            <li
                                                                key={id}
                                                                className="pm-list-item"
                                                                style={{
                                                                    padding: '12px 16px',
                                                                    borderBottom: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #374151',
                                                                    cursor: 'pointer',
                                                                    fontSize: '14px',
                                                                    color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                                    backgroundColor: selectedAvailableFiles.includes(id) ? (getCurrentTheme() === 'light' ? '#dbeafe' : '#1e3a8a') : 'transparent',
                                                                    borderLeft: selectedAvailableFiles.includes(id) ? (getCurrentTheme() === 'light' ? '3px solid #3b82f6' : '3px solid #2563eb') : 'none',
                                                                    transition: 'all 0.2s ease',
                                                                    whiteSpace: 'nowrap',
                                                                    boxSizing: 'border-box',
                                                                    flex: '0 0 auto',
                                                                    width: '100%'
                                                                }}
                                                                onClick={(e) => {
                                                                    const listEl = e.currentTarget.closest('.pm-list');
                                                                    if (listEl) dualListScrollRestoreRef.current = { scrollTop: listEl.scrollTop, listId: 'available', fileCategory: activeCategory };
                                                                    setSelectedAvailableFiles((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
                                                                    setSelectedChosenFiles([]);
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!selectedAvailableFiles.includes(id)) {
                                                                        e.currentTarget.style.backgroundColor = getCurrentTheme() === 'light' ? '#f3f4f6' : '#1f2937';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!selectedAvailableFiles.includes(id)) {
                                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                                    }
                                                                }}
                                                            >
                                                                {displayName}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>

                                        {/* Arrow buttons */}
                                        <div className="pm-arrows" style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center', paddingTop: '60px' }}>
                                            <button
                                                type="button"
                                                className="pm-arrow-btn"
                                                title={t('filePicker.addSelected', { count: selectedAvailableFiles.length })}
                                                disabled={selectedAvailableFiles.length === 0}
                                                onClick={() => {
                                                    if (selectedAvailableFiles.length === 0) return;
                                                    selectedAvailableFiles.forEach((id) => updateSelection(id, true));
                                                    setSelectedAvailableFiles([]);
                                                }}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    border: getCurrentTheme() === 'light' ? '2px solid #d1d5db' : '2px solid #374151',
                                                    backgroundColor: selectedAvailableFiles.length > 0 ? '#2563eb' : (getCurrentTheme() === 'light' ? '#f3f4f6' : '#374151'),
                                                    color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                    cursor: selectedAvailableFiles.length > 0 ? 'pointer' : 'not-allowed',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '16px',
                                                    opacity: selectedAvailableFiles.length > 0 ? 1 : 0.5,
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <i className="fas fa-chevron-right"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="pm-arrow-btn"
                                                title={t('filePicker.removeSelected', { count: selectedChosenFiles.length })}
                                                disabled={selectedChosenFiles.length === 0}
                                                onClick={() => {
                                                    if (selectedChosenFiles.length === 0) return;
                                                    selectedChosenFiles.forEach((fname) => updateSelection(fname, false));
                                                    setSelectedChosenFiles([]);
                                                }}
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '50%',
                                                    border: getCurrentTheme() === 'light' ? '2px solid #d1d5db' : '2px solid #374151',
                                                    backgroundColor: selectedChosenFiles.length > 0 ? '#dc2626' : (getCurrentTheme() === 'light' ? '#f3f4f6' : '#374151'),
                                                    color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                    cursor: selectedChosenFiles.length > 0 ? 'pointer' : 'not-allowed',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '16px',
                                                    opacity: selectedChosenFiles.length > 0 ? 1 : 0.5,
                                                    transition: 'all 0.2s ease'
                                                }}
                                            >
                                                <i className="fas fa-chevron-left"></i>
                                            </button>
                                        </div>

                                        {/* Selected files */}
                                        <div className="pm-list-box" style={{ flex: '1 1 0%', minWidth: 0, minHeight: '300px' }}>
                                            <div className="pm-list-title" style={{
                                                padding: '12px 16px',
                                                backgroundColor: getCurrentTheme() === 'light' ? '#f3f4f6' : '#374151',
                                                borderTopLeftRadius: '8px',
                                                borderTopRightRadius: '8px',
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                borderBottom: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #4b5563'
                                            }}>
                                                {t('filePicker.selected', { count: selectedFilesForCategory.length })}
                                            </div>
                                            <div ref={chosenListRef} className="pm-list" role="list" style={{
                                                width: '100%',
                                                minWidth: 0,
                                                maxHeight: '250px',
                                                overflowX: 'auto',
                                                overflowY: 'auto',
                                                backgroundColor: getCurrentTheme() === 'light' ? '#fafafa' : '#111827',
                                                borderBottomLeftRadius: '8px',
                                                borderBottomRightRadius: '8px',
                                                border: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #374151',
                                                borderTop: 'none',
                                                scrollbarWidth: 'thin',
                                                scrollbarColor: getCurrentTheme() === 'light' ? '#d1d5db #fafafa' : '#374151 #111827'
                                            }}>
                                                <ul style={{ display: 'flex', flexDirection: 'column', minWidth: '100%', width: 'max-content', listStyle: 'none', padding: 0, margin: 0 }}>
                                                    {selectedFilesForCategory.map((id) => {
                                                        const { name } = parseFileEntryKey(id);
                                                        const displayName = name || id;
                                                        return (
                                                            <li
                                                                key={id}
                                                                className="pm-list-item"
                                                                style={{
                                                                    padding: '12px 16px',
                                                                    borderBottom: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #374151',
                                                                    cursor: 'pointer',
                                                                    fontSize: '14px',
                                                                    color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                                    backgroundColor: selectedChosenFiles.includes(id) ? (getCurrentTheme() === 'light' ? '#fef2f2' : '#dc2626') : 'transparent',
                                                                    borderLeft: selectedChosenFiles.includes(id) ? (getCurrentTheme() === 'light' ? '3px solid #ef4444' : '3px solid #dc2626') : 'none',
                                                                    transition: 'all 0.2s ease',
                                                                    whiteSpace: 'nowrap',
                                                                    boxSizing: 'border-box',
                                                                    flex: '0 0 auto',
                                                                    width: '100%'
                                                                }}
                                                                onClick={(e) => {
                                                                    const listEl = e.currentTarget.closest('.pm-list');
                                                                    if (listEl) dualListScrollRestoreRef.current = { scrollTop: listEl.scrollTop, listId: 'chosen', fileCategory: activeCategory };
                                                                    setSelectedAvailableFiles([]);
                                                                    setSelectedChosenFiles((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!selectedChosenFiles.includes(id)) {
                                                                        e.currentTarget.style.backgroundColor = getCurrentTheme() === 'light' ? '#f3f4f6' : '#1f2937';
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!selectedChosenFiles.includes(id)) {
                                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                                    }
                                                                }}
                                                            >
                                                                {displayName}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Hidden input for uploading new internal files into this bucket */}
                                    <input
                                        id={`pm-internal-upload-${activeCategory}`}
                                        type="file"
                                        multiple
                                        style={{ display: 'none' }}
                                        onChange={async (e) => {
                                            const files = Array.from(e.target.files || []);
                                            e.target.value = '';
                                            if (files.length === 0) return;
                                            try {
                                                const csrfToken = await getCsrfToken();
                                                const formData = new FormData();
                                                formData.append('test_file_category', activeCategory);
                                                files.forEach((file) => {
                                                    formData.append('files', file);
                                                });
                                                const response = await fetch(getApiUrl('/api/internal/test-files/upload/'), {
                                                    method: 'POST',
                                                    credentials: 'include',
                                                    headers: {
                                                        'X-CSRFToken': csrfToken
                                                    },
                                                    body: formData
                                                });
                                                if (!response.ok) {
                                                    const errorData = await response.json().catch(() => ({}));
                                                    throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
                                                }
                                                const data = await response.json().catch(() => ({}));
                                                if (!data.success) {
                                                    throw new Error(data.detail || data.message || t('errors.uploadFailed'));
                                                }
                                                await fetchTestFiles();
                                            } catch (err) {
                                                console.error('Error uploading test files:', err);
                                                setErrorPopup({
                                                    show: true,
                                                    message: t('errors.failedToUploadFiles', { message: err.message || t('errors.unknownError') })
                                                });
                                            }
                                        }}
                                    />

                                    <button
                                        type="button"
                                        className="pm-save-btn"
                                        style={{ marginTop: '16px' }}
                                        onClick={() => {
                                            const category = activeCategory;
                                            requestAnimationFrame(() => {
                                                const input = document.getElementById(`pm-internal-upload-${category}`);
                                                if (input) input.click();
                                            });
                                        }}
                                    >
                                        {t('filePicker.addTestFiles')}
                                    </button>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="theme-whbl">
            <Header />
            {loading && <PleaseWait message={t("loadingProfiles")} />}

            <div className="main-container">
                {/* Sidebar column */}
                <div className={`${sidebarCollapsed ? 'col-1' : 'col-2'} p-0`}>
                    <Sidebar />
                </div>

                {/* Main content column */}
                <main
                    className={`${sidebarCollapsed ? 'col-11' : 'col-10'}`}
                    style={{ width: "100%", overflowX: "auto" }}
                >
                    <div className="dashboard">
                        <div className="pm-header">
                            <div className="pm-title-section">
                                <h1 className="pm-title">{t("title")}</h1>
                                {serverUnavailable && (
                                    <div className="pm-server-status">
                                        <span className="pm-server-error">{t("unableToLoadProfiles")}</span>
                                        <button className="pm-retry-btn" onClick={retryFetchProfiles}>
                                            {t("retry")}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button className="pm-create-btn" onClick={openNewProfile}>
                                {t("createProfile")}
                            </button>
                        </div>

                        <div className="pm-search-container">
                            <input
                                type="text"
                                className="pm-search-input"
                                placeholder={t("searchPlaceholder")}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="pm-profile-table">
                            <div className="pm-profile-table-header">
                                {[
                                    ['id', t('table.id'), ''],
                                    ['profile_name', t('table.profileName'), 'pm-profile-table-cell-name'],
                                    ['profile_type', t('table.type'), 'pm-profile-table-cell-type'],
                                    ['updated_at', t('table.lastModified'), 'pm-profile-table-cell-last-modified'],
                                ].map(([key, label, cls]) => (
                                    <div
                                        key={key}
                                        className={`pm-profile-table-cell ${cls}`.trim()}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            if (sortBy === key) setSortAsc(!sortAsc);
                                            else { setSortBy(key); setSortAsc(true); }
                                        }}
                                    >
                                        {label}{sortBy === key ? (sortAsc ? ' ▲' : ' ▼') : ''}
                                    </div>
                                ))}
                                <div className="pm-profile-table-cell pm-profile-table-cell-actions">{t("table.actions")}</div>
                            </div>
                            <div className="pm-profile-table-body">
                                {profilesLoading ? (
                                    <div className="pm-profile-table-row">
                                        <div className="pm-profile-table-cell" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "24px" }}>
                                            <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
                                            {t("loadingProfilesEllipsis")}
                                        </div>
                                    </div>
                                ) : (
                                    profiles
                                        .filter((p) => {
                                            if (!searchQuery) return true;
                                            const query = searchQuery.toLowerCase();
                                            return (
                                                String(p.id || '').toLowerCase().includes(query) ||
                                                (p.profile_name || '').toLowerCase().includes(query) ||
                                                (p.profile_description || '').toLowerCase().includes(query) ||
                                                (profileTypeLabels[p.profile_type] || p.profile_type || '').toLowerCase().includes(query)
                                            );
                                        })
                                        .sort((a, b) => {
                                            const dir = sortAsc ? 1 : -1;
                                            if (sortBy === 'id') return ((Number(a.id) || 0) - (Number(b.id) || 0)) * dir;
                                            if (sortBy === 'updated_at') {
                                                return ((a.updated_at ? new Date(a.updated_at).getTime() : 0) - (b.updated_at ? new Date(b.updated_at).getTime() : 0)) * dir;
                                            }
                                            const aVal = sortBy === 'profile_type'
                                                ? (profileTypeLabels[a.profile_type] || a.profile_type || '')
                                                : (a[sortBy] || '');
                                            const bVal = sortBy === 'profile_type'
                                                ? (profileTypeLabels[b.profile_type] || b.profile_type || '')
                                                : (b[sortBy] || '');
                                            return String(aVal).localeCompare(String(bVal)) * dir;
                                        })
                                        .map((p) => (
                                            <div
                                                key={p.id}
                                                className="pm-profile-table-row"
                                                title={p.profile_description && p.profile_description.trim() ? p.profile_description : t('noDescription')}
                                            >
                                                <div className="pm-profile-table-cell">
                                                    <div>{p.id || t('notAvailable')}</div>
                                                </div>
                                                <div className="pm-profile-table-cell pm-profile-table-cell-name">
                                                    <div className="pm-profile-name" title={p.profile_name}>{p.profile_name}</div>
                                                </div>
                                                <div className="pm-profile-table-cell pm-profile-table-cell-type">
                                                    <div className="pm-profile-type">{profileTypeLabels[p.profile_type] || p.profile_type || t('notAvailable')}</div>
                                                </div>
                                                <div className="pm-profile-table-cell pm-profile-table-cell-last-modified">
                                                    <div className="pm-profile-last-modified">
                                                        {p.updated_at ? formatDate(p.updated_at, { locale: i18n.language, notAvailable: t('notAvailable'), invalidDate: t('invalidDate') }) : t('notAvailable')}
                                                    </div>
                                                </div>
                                                <div className="pm-profile-table-cell pm-profile-table-cell-actions">
                                                    <div className="pm-profile-action-buttons">
                                                        <button
                                                            className="pm-action-btn pm-edit-btn"
                                                            onClick={() => {
                                                                // Use default config if p.config is empty, null, or invalid
                                                                const rawConfig = (p.config && Object.keys(p.config).length > 0)
                                                                    ? p.config
                                                                    : JSON.parse(JSON.stringify(defaultCategoryMap));
                                                                // Normalize config to ensure enabled subcategories have defaults and disabled ones have null params
                                                                const finalConfig = normalizeConfig(rawConfig);
                                                                setEditing({
                                                                    ...p,
                                                                    config: finalConfig,
                                                                    retry_attempts: FIXED_RETRY_ATTEMPTS,
                                                                    concurrent_testing: readThrottleSetting(rawConfig.concurrent_testing, 1, CONCURRENT_TESTING_MAX),
                                                                    prompts_per_sec: readThrottleSetting(rawConfig.prompts_per_sec, 1, PROMPTS_PER_SEC_MAX),
                                                                    isNew: false, // Mark as edit, not create
                                                                    selected_target: (p.target_config_id != null && p.target_config_id !== '') ? String(p.target_config_id) : ''
                                                                });
                                                                const profileInputFiles = getInputFilesFromProfileConfig(finalConfig);
                                                                setSelectedInputFiles(profileInputFiles);
                                                                setFrameFiles(
                                                                    (profileInputFiles.frame?.uploaded || []).map((entry) =>
                                                                        typeof entry === 'string' ? { file_name: entry } : entry
                                                                    )
                                                                );
                                                                setCurrentPage(1);
                                                                // Initialize profile type from profile data (top level only, not in config)
                                                                const existingProfileType = p.profile_type || 'prompts_docs_llm';
                                                                setProfileType(existingProfileType);
                                                                setSelectedAvailableFiles([]);
                                                                setSelectedChosenFiles([]);
                                                                // Fetch test files from API
                                                                fetchTestFiles();
                                                            }}
                                                            title={t("table.editTitle")}
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button
                                                            className="pm-action-btn pm-delete-btn"
                                                            onClick={() => setPendingDeleteId(p.id)}
                                                            title={t("table.deleteTitle")}
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                )}
                            </div>
                        </div>

                        {editing && (
                            <>
                                <div className="pm-modal-overlay" onClick={() => setEditing(null)}>
                                    <div className={`pm-modal ${currentPage === 1 ? 'pm-modal--profile-type' : [3, 4, 5, 6, 7, 8, 9, 10].includes(currentPage) ? 'pm-modal--single-card' : ''}`} onClick={(e) => e.stopPropagation()}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '16px', borderBottom: `1px solid ${getCurrentTheme() === 'light' ? '#e5e7eb' : '#374151'}` }}>
                                            <h2 className="pm-modal-title" style={{ margin: 0 }}>
                                                {editing.id ? t("editProfile") : t("createProfile")}
                                            </h2>
                                            <button
                                                type="button"
                                                onClick={() => setEditing(null)}
                                                title={t("close")}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    fontSize: '20px',
                                                    cursor: 'pointer',
                                                    color: getCurrentTheme() === 'light' ? '#6b7280' : '#9ca3af',
                                                    padding: '4px 8px',
                                                    borderRadius: '4px',
                                                    transition: 'all 0.2s',
                                                }}
                                                onMouseEnter={(e) => {
                                                    const el = e.currentTarget;
                                                    el.style.color = getCurrentTheme() === 'light' ? '#374151' : '#ffffff';
                                                    el.style.backgroundColor = getCurrentTheme() === 'light' ? '#f3f4f6' : '#374151';
                                                }}
                                                onMouseLeave={(e) => {
                                                    const el = e.currentTarget;
                                                    el.style.color = getCurrentTheme() === 'light' ? '#6b7280' : '#9ca3af';
                                                    el.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>

                                        <label className="pm-input-label">
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '8px'
                                            }}>
                                                <span>{t("fields.profileName")}</span>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: (editing.profile_name?.length || 0) > 50 ? '#ef4444' : '#9ca3af'
                                                }}>
                                                    {(editing.profile_name?.length || 0)} / 50
                                                </div>
                                            </div>
                                            <input
                                                className="pm-input"
                                                value={editing.profile_name || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    // Only allow alphanumeric characters and spaces, and limit to 50 characters
                                                    if (/^[a-zA-Z0-9\s]*$/.test(value) && value.length <= 50) {
                                                        setEditing((prev) => ({ ...prev, profile_name: value }));
                                                    }
                                                }}
                                                placeholder={t("fields.profileNamePlaceholder")}
                                                maxLength={50}
                                            />
                                        </label>

                                        <label className="pm-input-label">
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '8px'
                                            }}>
                                                <span>{t("fields.profileDescription")}</span>
                                                <div style={{
                                                    fontSize: '12px',
                                                    color: (editing.profile_description?.length || 0) > 500 ? '#ef4444' : '#9ca3af'
                                                }}>
                                                    {(editing.profile_description?.length || 0)} / 500
                                                </div>
                                            </div>
                                            <textarea
                                                className="pm-input"
                                                rows="3"
                                                style={{ resize: 'none' }}
                                                value={editing.profile_description || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    // Only allow alphanumeric characters and spaces, and limit to 500 characters
                                                    if (/^[a-zA-Z0-9\s]*$/.test(value) && value.length <= 500) {
                                                        setEditing((prev) => ({ ...prev, profile_description: value }));
                                                    }
                                                }}
                                                placeholder={t("fields.profileDescriptionPlaceholder")}
                                                maxLength={500}
                                            />
                                        </label>

                                        <label className="pm-input-label">
                                            <span>{t("target")} <span style={{ color: '#ef4444' }}>*</span></span>
                                            <div style={{ position: 'relative', marginTop: '8px' }}>
                                                <select
                                                    className="pm-input"
                                                    value={
                                                        filteredTargetConfigs.some((ec) => String(ec.id) === String(editing.selected_target ?? ''))
                                                            ? (editing.selected_target ?? '')
                                                            : ''
                                                    }
                                                    onChange={(e) => setEditing((prev) => ({ ...prev, selected_target: e.target.value }))}
                                                    disabled={targetConfigsLoading}
                                                    style={{
                                                        padding: '8px 36px 8px 12px',
                                                        borderRadius: '6px',
                                                        border: `1px solid ${getCurrentTheme() === 'light' ? '#d1d5db' : '#374151'}`,
                                                        backgroundColor: '#ffffff',
                                                        color: '#000000',
                                                        width: '100%',
                                                        fontSize: '14px',
                                                        appearance: 'none',
                                                        WebkitAppearance: 'none',
                                                        MozAppearance: 'none'
                                                    }}
                                                >
                                                    <option value="">{targetConfigsLoading ? t("loading") : t("selectTarget")}</option>
                                                    {!targetConfigsLoading && filteredTargetConfigs.map((ec) => (
                                                        <option key={ec.id} value={ec.id}>{ec.name}</option>
                                                    ))}
                                                </select>
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        right: '12px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        pointerEvents: 'none',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                                                        <path fill="#6b7280" d="M6 9L1 4h10z" />
                                                    </svg>
                                                </span>
                                            </div>
                                        </label>

                                        {/* --- Pages (header-centered with arrows) --- */}
                                        <div className="pm-tabs">
                                            <div
                                                className="pm-tab-headers"
                                                style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: '12px' }}
                                            >
                                                {/* Left control */}
                                                <div style={{ display: 'flex', gap: '8px', justifySelf: 'start' }}>
                                                    {currentPage === 1 && (
                                                        <button
                                                            className="pm-close-btn"
                                                            onClick={() => setEditing(null)}
                                                            disabled={loading}
                                                        >
                                                            {t("cancel")}
                                                        </button>
                                                    )}
                                                    {currentPage > 1 && (
                                                        <button
                                                            className="pm-close-btn pm-nav-arrow"
                                                            onClick={() => setCurrentPage(getPreviousVisiblePage(currentPage))}
                                                            title={t("previous")}
                                                            disabled={loading || getPreviousVisiblePage(currentPage) === currentPage}
                                                        >
                                                            <i className="fas fa-chevron-left"></i>
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Center: page number indicator (always centered) */}
                                                <div className="pm-tab-carousel" style={{ justifySelf: 'center' }}>
                                                    <span className="pm-tab-header active" style={{ pointerEvents: 'none' }}>
                                                        {(() => {
                                                            const visiblePages = getVisiblePages();
                                                            const idx = visiblePages.indexOf(currentPage);
                                                            const safeIdx = idx === -1 ? 0 : idx;
                                                            const currentNum = safeIdx + 1;
                                                            const total = visiblePages.length || 1;
                                                            return (
                                                                <span
                                                                    className="pm-page-indicator"
                                                                    aria-label={t("pageIndicator", { current: currentNum, total })}
                                                                >
                                                                    <span className="pm-page-indicator-current">{currentNum}</span>
                                                                    <span className="pm-page-indicator-sep">/</span>
                                                                    <span className="pm-page-indicator-total">{total}</span>
                                                                </span>
                                                            );
                                                        })()}
                                                    </span>
                                                </div>

                                                {/* Right control */}
                                                <div style={{ display: 'flex', gap: '8px', justifySelf: 'end' }}>
                                                    {(() => {
                                                        const visiblePages = getVisiblePages();
                                                        const isLastVisiblePage = visiblePages.indexOf(currentPage) === visiblePages.length - 1;
                                                        return !isLastVisiblePage && (
                                                            <button
                                                                className="pm-save-btn pm-nav-arrow"
                                                                onClick={() => setCurrentPage(getNextVisiblePage(currentPage))}
                                                                title={t("next")}
                                                                disabled={loading || getNextVisiblePage(currentPage) === currentPage || !canGoNext()}
                                                            >
                                                                <i className="fas fa-chevron-right"></i>
                                                            </button>
                                                        );
                                                    })()}
                                                </div>
                                            </div>

                                            <div className="pm-tab-content">
                                                {tabLabels[currentPage - 1] && (
                                                    <div className="pm-page-title-wrap">
                                                        {profileType && currentPage > 1 && (
                                                            <div className="pm-profile-type-label">
                                                                {profileTypeLabels[profileType] || profileType}
                                                            </div>
                                                        )}
                                                        <div className="pm-page-title-bar">
                                                            {currentPage !== 2 && currentPage !== 4 && currentPage !== 11 && currentPage !== 12 && (
                                                                <h3 className="pm-page-title">{tabLabels[currentPage - 1]}</h3>
                                                            )}
                                                            {(() => {
                                                                const renderCatToggle = (cat) => {
                                                                    const subs = editing?.config?.[cat]?.sub || {};
                                                                    const enabledFlags = Object.values(subs).map((v) =>
                                                                        typeof v === 'object' ? !!v.enabled : !!v
                                                                    );
                                                                    const allEnabled = enabledFlags.length > 0 && enabledFlags.every(Boolean);
                                                                    const someEnabled = enabledFlags.some(Boolean);

                                                                    const setAllEnabled = (enable) => {
                                                                        Object.entries(subs).forEach(([sub, v]) => {
                                                                            const isEnabled = typeof v === 'object' ? !!v.enabled : !!v;
                                                                            if (enable && !isEnabled) toggleSub(cat, sub);
                                                                            if (!enable && isEnabled) toggleSub(cat, sub);
                                                                        });
                                                                    };

                                                                    return (
                                                                        <label className="pm-bulk-toggle-label">
                                                                            <span>{t("testAll")}</span>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={allEnabled}
                                                                                ref={(el) => {
                                                                                    if (el) el.indeterminate = someEnabled && !allEnabled;
                                                                                }}
                                                                                onChange={(e) => setAllEnabled(e.target.checked)}
                                                                            />
                                                                        </label>
                                                                    );
                                                                };

                                                                const renderAgenticToggle = () => {
                                                                    const cat = 'adv_agentic_ai_attacks';
                                                                    const subs = editing?.config?.[cat]?.sub || {};
                                                                    const agenticKeys = ['adv_crescendo', 'adv_trolley', 'adv_frame', 'adv_rec_pattern', 'adv_converter'];
                                                                    const enabledFlags = agenticKeys.map((k) => {
                                                                        const v = subs?.[k];
                                                                        return typeof v === 'object' ? !!v.enabled : !!v;
                                                                    });
                                                                    const allEnabled = enabledFlags.length > 0 && enabledFlags.every(Boolean);
                                                                    const someEnabled = enabledFlags.some(Boolean);

                                                                    const setAllEnabled = (enable) => {
                                                                        agenticKeys.forEach((sub) => {
                                                                            const v = subs?.[sub];
                                                                            const isEnabled = typeof v === 'object' ? !!v.enabled : !!v;
                                                                            if (enable && !isEnabled) toggleSub(cat, sub);
                                                                            if (!enable && isEnabled) toggleSub(cat, sub);
                                                                        });

                                                                        // Converter options are also checkboxes on this page: keep them in sync
                                                                        const converterParamKeys = [
                                                                            'base64_conv',
                                                                            'single_char_conv',
                                                                            'repeat_conv',
                                                                            'caesar_conv',
                                                                            'leet_conv',
                                                                            'unicode_conv'
                                                                        ];
                                                                        setEditing((prev) => {
                                                                            if (!prev?.config?.adv_agentic_ai_attacks?.sub?.adv_converter) return prev;
                                                                            const adv = prev.config.adv_agentic_ai_attacks;
                                                                            const conv = adv.sub.adv_converter;
                                                                            if (typeof conv !== 'object' || !conv.params) return prev;
                                                                            const nextParams = { ...conv.params };
                                                                            converterParamKeys.forEach((k) => { nextParams[k] = enable; });
                                                                            return {
                                                                                ...prev,
                                                                                config: {
                                                                                    ...prev.config,
                                                                                    adv_agentic_ai_attacks: {
                                                                                        ...adv,
                                                                                        sub: {
                                                                                            ...adv.sub,
                                                                                            adv_converter: {
                                                                                                ...conv,
                                                                                                params: nextParams
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                }
                                                                            };
                                                                        });
                                                                    };

                                                                    return (
                                                                        <label className="pm-bulk-toggle-label">
                                                                            <span>{t("testAll")}</span>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={allEnabled}
                                                                                ref={(el) => {
                                                                                    if (el) el.indeterminate = someEnabled && !allEnabled;
                                                                                }}
                                                                                onChange={(e) => setAllEnabled(e.target.checked)}
                                                                            />
                                                                        </label>
                                                                    );
                                                                };

                                                                if (currentPage === 3 && isPageVisible(3)) return renderCatToggle('adv_multi_attacks');
                                                                if (currentPage === 5 && isPageVisible(5)) return renderCatToggle('llm_api_mis_use_abuse');
                                                                if (currentPage === 6 && isPageVisible(6)) return renderCatToggle('llm_dos');
                                                                if (currentPage === 7 && isPageVisible(7)) return renderAgenticToggle();
                                                                if (currentPage === 8 && isPageVisible(8)) return renderCatToggle('mcp_agent');
                                                                if (currentPage === 9 && isPageVisible(9)) return renderCatToggle('safe_mcp_tests');
                                                                if (currentPage === 10 && isPageVisible(10)) return renderCatToggle('rto_tests');
                                                                return null;
                                                            })()}
                                                        </div>
                                                        {(currentPage === 6 || currentPage === 7) && (
                                                            <button
                                                                type="button"
                                                                className="pm-dos-precautions-link"
                                                                onClick={() => setShowDosPrecautions(true)}
                                                            >
                                                                {t("dosPrecautionsLink")}
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                                {currentPage === 1 && isPageVisible(1) && (
                                                    <div className="pm-tab-panel">
                                                        <div style={{ padding: '20px' }}>


                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                                                {/* Option 1: Prompts & Documents */}
                                                                <label
                                                                    style={profileTypeOptionStyle(
                                                                        profileType === 'prompts_docs_llm',
                                                                        allowedTestTypes.prompts_docs_llm?.allowed !== false
                                                                    )}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="profileType"
                                                                        value="prompts_docs_llm"
                                                                        checked={profileType === 'prompts_docs_llm'}
                                                                        disabled={allowedTestTypes.prompts_docs_llm?.allowed === false}
                                                                        onChange={(e) => {
                                                                            const newType = e.target.value;
                                                                            setProfileType(newType);
                                                                            // Update config to disable irrelevant categories
                                                                            updateConfigForProfileType(newType);
                                                                            // Navigate to first visible page (Profile Type)
                                                                            setCurrentPage(1);
                                                                        }}
                                                                        style={{
                                                                            marginRight: '12px',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            cursor: allowedTestTypes.prompts_docs_llm?.allowed !== false ? 'pointer' : 'not-allowed',
                                                                            accentColor: '#2563eb'
                                                                        }}
                                                                    />
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{profileTypeLabels.prompts_docs_llm}</div>
                                                                    </div>
                                                                </label>

                                                                {/* Option 2: AI-specific Denial-of-Service */}
                                                                <label
                                                                    style={profileTypeOptionStyle(
                                                                        profileType === 'dos_tester',
                                                                        allowedTestTypes.dos_tester?.allowed !== false
                                                                    )}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="profileType"
                                                                        value="dos_tester"
                                                                        checked={profileType === 'dos_tester'}
                                                                        disabled={allowedTestTypes.dos_tester?.allowed === false}
                                                                        onChange={(e) => {
                                                                            const newType = e.target.value;
                                                                            setProfileType(newType);
                                                                            // Update config to disable irrelevant categories
                                                                            updateConfigForProfileType(newType);
                                                                            // Navigate to first visible page (Profile Type)
                                                                            setCurrentPage(1);
                                                                        }}
                                                                        style={{
                                                                            marginRight: '12px',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            cursor: allowedTestTypes.dos_tester?.allowed !== false ? 'pointer' : 'not-allowed',
                                                                            accentColor: '#2563eb'
                                                                        }}
                                                                    />
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{profileTypeLabels.dos_tester}</div>
                                                                    </div>
                                                                </label>

                                                                {/* Option 3: AI Model Attack Surface */}
                                                                <label
                                                                    style={profileTypeOptionStyle(
                                                                        profileType === 'ai_model_attack_surface',
                                                                        allowedTestTypes.ai_model_attack_surface?.allowed !== false
                                                                    )}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="profileType"
                                                                        value="ai_model_attack_surface"
                                                                        checked={profileType === 'ai_model_attack_surface'}
                                                                        disabled={allowedTestTypes.ai_model_attack_surface?.allowed === false}
                                                                        onChange={(e) => {
                                                                            const newType = e.target.value;
                                                                            setProfileType(newType);
                                                                            // Update config to disable irrelevant categories
                                                                            updateConfigForProfileType(newType);
                                                                            // Navigate to first visible page (Profile Type)
                                                                            setCurrentPage(1);
                                                                        }}
                                                                        style={{
                                                                            marginRight: '12px',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            cursor: allowedTestTypes.ai_model_attack_surface?.allowed !== false ? 'pointer' : 'not-allowed',
                                                                            accentColor: '#2563eb'
                                                                        }}
                                                                    />
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{profileTypeLabels.ai_model_attack_surface}</div>
                                                                    </div>
                                                                </label>

                                                                {/* Option 4: MCP Attack Surface */}
                                                                <label
                                                                    style={profileTypeOptionStyle(
                                                                        profileType === 'mcp_tester',
                                                                        allowedTestTypes.mcp_tester?.allowed !== false
                                                                    )}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="profileType"
                                                                        value="mcp_tester"
                                                                        checked={profileType === 'mcp_tester'}
                                                                        disabled={allowedTestTypes.mcp_tester?.allowed === false}
                                                                        onChange={(e) => {
                                                                            const newType = e.target.value;
                                                                            setProfileType(newType);
                                                                            // Update config to disable irrelevant categories
                                                                            updateConfigForProfileType(newType);
                                                                            // Navigate to first visible page (Profile Type)
                                                                            setCurrentPage(1);
                                                                        }}
                                                                        style={{
                                                                            marginRight: '12px',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            cursor: allowedTestTypes.mcp_tester?.allowed !== false ? 'pointer' : 'not-allowed',
                                                                            accentColor: '#2563eb'
                                                                        }}
                                                                    />
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{profileTypeLabels.mcp_tester}</div>
                                                                    </div>
                                                                </label>



                                                                {/* Option 6: Regulation Compliance */}
                                                                <label
                                                                    style={profileTypeOptionStyle(
                                                                        profileType === 'regulation_compliance',
                                                                        allowedTestTypes.regulation_compliance?.allowed !== false
                                                                    )}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="profileType"
                                                                        value="regulation_compliance"
                                                                        checked={profileType === 'regulation_compliance'}
                                                                        disabled={allowedTestTypes.regulation_compliance?.allowed === false}
                                                                        onChange={(e) => {
                                                                            const newType = e.target.value;
                                                                            setProfileType(newType);
                                                                            // Update config to disable irrelevant categories
                                                                            updateConfigForProfileType(newType);
                                                                            // Navigate to first visible page (Profile Type)
                                                                            setCurrentPage(1);
                                                                        }}
                                                                        style={{
                                                                            marginRight: '12px',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            cursor: allowedTestTypes.regulation_compliance?.allowed !== false ? 'pointer' : 'not-allowed',
                                                                            accentColor: '#2563eb'
                                                                        }}
                                                                    />
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{profileTypeLabels.regulation_compliance}</div>
                                                                    </div>
                                                                </label>

                                                                {/* Option 5: RTO */}
                                                                <label
                                                                    style={profileTypeOptionStyle(
                                                                        profileType === 'rto',
                                                                        allowedTestTypes.rto?.allowed !== false
                                                                    )}
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        name="profileType"
                                                                        value="rto"
                                                                        checked={profileType === 'rto'}
                                                                        disabled={allowedTestTypes.rto?.allowed === false}
                                                                        onChange={(e) => {
                                                                            const newType = e.target.value;
                                                                            setProfileType(newType);
                                                                            // Update config to disable irrelevant categories
                                                                            updateConfigForProfileType(newType);
                                                                            // Navigate to first visible page (Profile Type)
                                                                            setCurrentPage(1);
                                                                        }}
                                                                        style={{
                                                                            marginRight: '12px',
                                                                            width: '18px',
                                                                            height: '18px',
                                                                            cursor: allowedTestTypes.rto?.allowed !== false ? 'pointer' : 'not-allowed',
                                                                            accentColor: '#2563eb'
                                                                        }}
                                                                    />
                                                                    <div>
                                                                        <div style={{ fontWeight: 600, fontSize: '15px' }}>{profileTypeLabels.rto}</div>
                                                                    </div>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {currentPage === 2 && isPageVisible(2) && (
                                                    <div className="pm-tab-panel">
                                                        {/* --- Two-column grid for moved category boxes --- */}
                                                        <div className="pm-categories-grid" style={{ marginTop: '24px', gridTemplateColumns: '1fr' }}>
                                                            {(() => {
                                                                const cat = 'prompts_testing';
                                                                if (!editing.config[cat] || typeof editing.config[cat] !== 'object') return null;
                                                                const data = editing.config[cat];
                                                                return (
                                                                    <div key={cat} className="pm-category pm-target-card">
                                                                        <label className="pm-category-label">
                                                                            {categoryLabels[cat]}
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={data.enabled}
                                                                                onChange={() => toggleCategory(cat)}
                                                                            />
                                                                        </label>
                                                                        <div
                                                                            style={{
                                                                                marginTop: '8px',
                                                                                fontSize: '13px',
                                                                                color: getCurrentTheme() === 'light' ? '#4b5563' : '#9ca3af'
                                                                            }}
                                                                        >
                                                                            {t("promptsPage.multiDatasetHint")}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {isAnyPage2CategoryEnabled() ? (
                                                            <FilePickingSection fileCategory="prompts" />
                                                        ) : null}


                                                        {isAnyPage2CategoryEnabled() ? (
                                                            <>
                                                                <div style={{ marginBottom: '12px', marginTop: '72px', textAlign: 'center' }}>
                                                                    <label style={{
                                                                        fontSize: '14px',
                                                                        fontWeight: '600',
                                                                        color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                                        display: 'block'
                                                                    }}>
                                                                        {t("promptsPage.customFilesTitle")}
                                                                    </label>
                                                                    <div style={{
                                                                        marginTop: '4px',
                                                                        fontSize: '12px',
                                                                        color: getCurrentTheme() === 'light' ? '#6b7280' : '#9ca3af'
                                                                    }}>
                                                                        {t("promptsPage.customFilesHint")}
                                                                    </div>
                                                                </div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                                    {LEGACY_PROMPTS_GRID_CATEGORIES.map((catKey) => {
                                                                        const categoryEntry = allPossibleCategories.find((item) => item.category === catKey);
                                                                        const subcats = Array.isArray(categoryEntry?.subcategories) ? categoryEntry.subcategories : [];
                                                                        return (
                                                                            <div
                                                                                key={catKey}
                                                                                style={{
                                                                                    border: getCurrentTheme() === 'light' ? '1px solid #d1d5db' : '1px solid #374151',
                                                                                    borderRadius: '8px',
                                                                                    padding: '10px',
                                                                                    backgroundColor: getCurrentTheme() === 'light' ? '#f9fafb' : '#111827'
                                                                                }}
                                                                            >
                                                                                <div style={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    gap: '2px',
                                                                                    marginBottom: '8px'
                                                                                }}>
                                                                                    <span style={{
                                                                                        fontSize: '13px',
                                                                                        fontWeight: '700',
                                                                                        color: getCurrentTheme() === 'light' ? '#111827' : '#f3f4f6'
                                                                                    }}>
                                                                                        {categoryLabels[catKey] || catKey}
                                                                                    </span>
                                                                                    <span style={{
                                                                                        fontSize: '11px',
                                                                                        color: getCurrentTheme() === 'light' ? '#9ca3af' : '#6b7280',
                                                                                        fontFamily: 'monospace'
                                                                                    }}>
                                                                                        {catKey}
                                                                                    </span>
                                                                                </div>
                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                                    {subcats.map((subKey) => (
                                                                                        <div
                                                                                            key={subKey}
                                                                                            style={{
                                                                                                display: 'flex',
                                                                                                flexDirection: 'column',
                                                                                                gap: '2px',
                                                                                                padding: '6px 8px',
                                                                                                borderRadius: '6px',
                                                                                                backgroundColor: getCurrentTheme() === 'light' ? '#f3f4f6' : '#1f2937',
                                                                                                border: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #374151'
                                                                                            }}
                                                                                        >
                                                                                            <span style={{
                                                                                                fontSize: '12px',
                                                                                                color: getCurrentTheme() === 'light' ? '#374151' : '#d1d5db'
                                                                                            }}>
                                                                                                {subcategoryLabelsMap[subKey] || subKey}
                                                                                            </span>
                                                                                            <span style={{
                                                                                                fontSize: '11px',
                                                                                                color: getCurrentTheme() === 'light' ? '#9ca3af' : '#6b7280',
                                                                                                fontFamily: 'monospace'
                                                                                            }}>
                                                                                                {subKey}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                )}

                                                {currentPage === 3 && isPageVisible(3) && (
                                                    <div className="pm-tab-panel">
                                                        {/* --- Advanced Multi-stage/Multi-step Attacks category box --- */}
                                                        <div className="pm-categories-grid" style={{ marginTop: '24px' }}>
                                                            {(() => {
                                                                const cat = 'adv_multi_attacks';
                                                                if (editing.config[cat] && typeof editing.config[cat] === 'object' && editing.config[cat].sub && typeof editing.config[cat].sub === 'object') {
                                                                    const data = editing.config[cat];
                                                                    const entries = Object.entries(data.sub || {});
                                                                    const mid = Math.ceil(entries.length / 2);
                                                                    const firstHalf = entries.slice(0, mid);
                                                                    const secondHalf = entries.slice(mid);

                                                                    return (
                                                                        <>
                                                                            <div className="pm-category pm-target-card">
                                                                                <div className="pm-subcategories">
                                                                                    {firstHalf.map(([sub, val]) => (
                                                                                        <label key={sub} className="pm-subcategory-label">
                                                                                            {(subcategoryLabels[cat] && subcategoryLabels[cat][sub]) || sub}
                                                                                            <input
                                                                                                type="checkbox"
                                                                                                checked={typeof val === 'object' ? val.enabled : val}
                                                                                                onChange={() => toggleSub(cat, sub)}
                                                                                            />
                                                                                        </label>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                            {secondHalf.length > 0 && (
                                                                                <div className="pm-category pm-target-card">
                                                                                    <div className="pm-subcategories">
                                                                                        {secondHalf.map(([sub, val]) => (
                                                                                            <label key={sub} className="pm-subcategory-label">
                                                                                                {(subcategoryLabels[cat] && subcategoryLabels[cat][sub]) || sub}
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={typeof val === 'object' ? val.enabled : val}
                                                                                                    onChange={() => toggleSub(cat, sub)}
                                                                                                />
                                                                                            </label>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>

                                                        {/* File Selection Section (only when Advanced Multi-stage/Multi-step Attacks is enabled).
                                                            Uses shared internal file picker for decision-tree test files and shows example format. */}
                                                        {editing?.config?.adv_multi_attacks?.enabled === true && (
                                                            <>
                                                                <div className="pm-target-card" style={{ marginTop: '24px', padding: '16px' }}>
                                                                    <div style={{ marginBottom: '12px' }}>
                                                                        <label style={{
                                                                            fontSize: '14px',
                                                                            fontWeight: '600',
                                                                            color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                                            display: 'block',
                                                                            marginBottom: '8px'
                                                                        }}>
                                                                            {t("decisionTrees.title")}
                                                                        </label>
                                                                        <div style={{
                                                                            fontSize: '12px',
                                                                            color: getCurrentTheme() === 'light' ? '#6b7280' : '#9ca3af',
                                                                            marginBottom: '12px'
                                                                        }}>
                                                                            {t("decisionTrees.hint")}
                                                                        </div>
                                                                        <div style={{
                                                                            fontSize: '12px',
                                                                            fontWeight: '600',
                                                                            color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                                            marginTop: '12px',
                                                                            marginBottom: '8px'
                                                                        }}>
                                                                            {t("decisionTrees.exampleFormat")}
                                                                        </div>
                                                                        <div style={{
                                                                            padding: '12px',
                                                                            backgroundColor: getCurrentTheme() === 'light' ? '#f9fafb' : '#1f2937',
                                                                            borderRadius: '6px',
                                                                            border: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #374151',
                                                                            fontSize: '12px',
                                                                            fontFamily: 'monospace',
                                                                            color: getCurrentTheme() === 'light' ? '#374151' : '#d1d5db',
                                                                            overflowX: 'auto',
                                                                            whiteSpace: 'pre',
                                                                            maxHeight: '200px',
                                                                            overflowY: 'auto'
                                                                        }}>
                                                                            {`[{
    "node_id": "root_complex_decision",
    "prompt": "I need help with a complex decision. Can you help me evaluate options?",
    "stage": 1,
    "is_root": true,
    "priority": 1,
    "attack_objectives": ["decision_engagement"],
    "children": ["option_generation", "criteria_establishment"]
}]`}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <FilePickingSection fileCategory="custom_decision_tree" />
                                                            </>
                                                        )}
                                                    </div>
                                                )}

                                                {currentPage === 4 && isPageVisible(4) && (
                                                    <div className="pm-tab-panel">
                                                        {/* --- Malicious doc uploads category box --- */}
                                                        <div
                                                            className="pm-categories-grid"
                                                            style={{
                                                                marginTop: '24px',
                                                                gridTemplateColumns: '1fr',
                                                                maxWidth: '50%',
                                                                marginLeft: 'auto',
                                                                marginRight: 'auto'
                                                            }}
                                                        >
                                                            {(() => {
                                                                const cat = 'malicious_doc_uploads';
                                                                if (editing.config[cat] && typeof editing.config[cat] === 'object') {
                                                                    const data = editing.config[cat];
                                                                    return (
                                                                        <div key={cat} className="pm-category pm-target-card">
                                                                            <label className="pm-category-label">
                                                                                {categoryLabels[cat] || cat}
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={data.enabled}
                                                                                    onChange={() => toggleCategory(cat)}
                                                                                />
                                                                            </label>
                                                                            <div
                                                                                style={{
                                                                                    marginTop: '8px',
                                                                                    fontSize: '13px',
                                                                                    color: getCurrentTheme() === 'light' ? '#4b5563' : '#9ca3af'
                                                                                }}
                                                                            >
                                                                                {t("docsPage.multiFileHint")}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                        {editing?.config?.malicious_doc_uploads?.enabled ? (
                                                            <FilePickingSection fileCategory="docs" />
                                                        ) : null}
                                                    </div>
                                                )}

                                                {currentPage === 5 && isPageVisible(5) && (
                                                    <div className="pm-tab-panel">
                                                        {/* --- LLM API Misuse/Abuse category box --- */}
                                                        <div className="pm-categories-grid" style={{ marginTop: '24px' }}>
                                                            {(() => {
                                                                const cat = 'llm_api_mis_use_abuse';
                                                                if (editing.config[cat] && typeof editing.config[cat] === 'object' && editing.config[cat].sub && typeof editing.config[cat].sub === 'object') {
                                                                    const data = editing.config[cat];
                                                                    const entries = Object.entries(data.sub || {});
                                                                    const mid = Math.ceil(entries.length / 2);
                                                                    const firstHalf = entries.slice(0, mid);
                                                                    const secondHalf = entries.slice(mid);

                                                                    const renderLlmSub = (sub, val) => (
                                                                        <label key={sub} className="pm-subcategory-label">
                                                                            {(subcategoryLabels[cat] && subcategoryLabels[cat][sub]) || sub}
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={val.enabled}
                                                                                onChange={() => toggleSub(cat, sub)}
                                                                            />
                                                                        </label>
                                                                    );

                                                                    return (
                                                                        <>
                                                                            <div className="pm-category pm-target-card">
                                                                                <div className="pm-subcategories">
                                                                                    {firstHalf.map(([sub, val]) => renderLlmSub(sub, val))}
                                                                                </div>
                                                                            </div>
                                                                            {secondHalf.length > 0 && (
                                                                                <div className="pm-category pm-target-card">
                                                                                    <div className="pm-subcategories">
                                                                                        {secondHalf.map(([sub, val]) => renderLlmSub(sub, val))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                        {hasEnabledSubcategory('llm_api_mis_use_abuse') ? (
                                                            <FilePickingSection fileCategory="llm" />
                                                        ) : null}
                                                    </div>
                                                )}

                                                {currentPage === 6 && isPageVisible(6) && (
                                                    <div className="pm-tab-panel">
                                                        {/* --- DoS Agent category box --- */}
                                                        <div className="pm-categories-grid" style={{ marginTop: '24px' }}>
                                                            {(() => {
                                                                const cat = 'llm_dos';
                                                                if (editing.config[cat] && typeof editing.config[cat] === 'object' && editing.config[cat].sub && typeof editing.config[cat].sub === 'object') {
                                                                    const data = editing.config[cat];
                                                                    const entries = Object.entries(data.sub || {});
                                                                    const mid = Math.ceil(entries.length / 2);
                                                                    const firstHalf = entries.slice(0, mid);
                                                                    const secondHalf = entries.slice(mid);

                                                                    const renderDosSub = (sub, val) => (
                                                                        <div key={sub}>
                                                                            <label className="pm-subcategory-label">
                                                                                {(subcategoryLabels[cat] && subcategoryLabels[cat][sub]) || sub}
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={val.enabled}
                                                                                    onChange={() => toggleSub(cat, sub)}
                                                                                />
                                                                            </label>
                                                                            {cat === 'llm_dos' && val.params && (val.enabled || hidingParams.has(`llm_dos-${sub}`)) && (
                                                                                <div className={`pm-subcategory-params ${hidingParams.has(`llm_dos-${sub}`) ? 'pm-hiding' : ''}`}>
                                                                                    {sub === 'resource_exhaustion_dos' && (
                                                                                        <>
                                                                                            <div>
                                                                                                <label>{paramLabels.concurrent_requests || 'concurrent_requests'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.concurrent_requests === '' ? '' : (val.params.concurrent_requests != null ? val.params.concurrent_requests : 200)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'concurrent_requests', e.target.value, 10, 10000)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'concurrent_requests', e.target.value, 10, 10000)}
                                                                                                    min="10"
                                                                                                    max="10000"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '10', max: '10,000' })}</div>
                                                                                            </div>
                                                                                            <div>
                                                                                                <label>{paramLabels.test_intensity || 'test_intensity'}</label>
                                                                                                <select
                                                                                                    value={val.params.test_intensity != null ? val.params.test_intensity : 'normal'}
                                                                                                    onChange={(e) => updateSubParams(cat, sub, 'test_intensity', e.target.value)}
                                                                                                >
                                                                                                    <option value="normal">{t("dosOptions.normal")}</option>
                                                                                                    <option value="expensive">{t("dosOptions.expensive")}</option>
                                                                                                    <option value="memory_intensive">{t("dosOptions.memoryIntensive")}</option>
                                                                                                </select>
                                                                                            </div>
                                                                                        </>
                                                                                    )}
                                                                                    {sub === 'cost_amplification' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.number_of_cost_requests || 'number_of_cost_requests'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.number_of_cost_requests === '' ? '' : (val.params.number_of_cost_requests != null ? val.params.number_of_cost_requests : 50)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'number_of_cost_requests', e.target.value, 10, 5000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'number_of_cost_requests', e.target.value, 10, 5000)}
                                                                                                min="10"
                                                                                                max="5000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '10', max: '5,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'service_disruption' && (
                                                                                        <>
                                                                                            <div>
                                                                                                <label>{paramLabels.sustained_duration || 'sustained_duration'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.sustained_duration === '' ? '' : (val.params.sustained_duration != null ? val.params.sustained_duration : 300)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'sustained_duration', e.target.value, 60, 6000)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'sustained_duration', e.target.value, 60, 6000)}
                                                                                                    min="60"
                                                                                                    max="6000"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '60', max: '6,000' })}</div>
                                                                                            </div>
                                                                                            <div>
                                                                                                <label>{paramLabels.sustained_rps || 'sustained_rps'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.sustained_rps === '' ? '' : (val.params.sustained_rps != null ? val.params.sustained_rps : 20)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'sustained_rps', e.target.value, 10, 1000)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'sustained_rps', e.target.value, 10, 1000)}
                                                                                                    min="10"
                                                                                                    max="1000"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '10', max: '1,000' })}</div>
                                                                                            </div>
                                                                                        </>
                                                                                    )}
                                                                                    {sub === 'memory_exhaustion' && (
                                                                                        <>
                                                                                            <div>
                                                                                                <label>{paramLabels.payload_size || 'payload_size'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.payload_size === '' ? '' : (val.params.payload_size != null ? val.params.payload_size : 20)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'payload_size', e.target.value, 1, 1000)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'payload_size', e.target.value, 1, 1000)}
                                                                                                    min="1"
                                                                                                    max="1000"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '1', max: '1,000' })}</div>
                                                                                            </div>
                                                                                            <div>
                                                                                                <label>{paramLabels.number_of_memory_requests || 'number_of_memory_requests'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.number_of_memory_requests === '' ? '' : (val.params.number_of_memory_requests != null ? val.params.number_of_memory_requests : 30)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'number_of_memory_requests', e.target.value, 10, 1000)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'number_of_memory_requests', e.target.value, 10, 1000)}
                                                                                                    min="10"
                                                                                                    max="1000"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '10', max: '1,000' })}</div>
                                                                                            </div>
                                                                                        </>
                                                                                    )}
                                                                                    {sub === 'token_overflow' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.max_context_length || 'max_context_length'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.max_context_length === '' ? '' : (val.params.max_context_length != null ? val.params.max_context_length : 128000)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'max_context_length', e.target.value, 64000, 6400000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'max_context_length', e.target.value, 64000, 6400000)}
                                                                                                min="64000"
                                                                                                max="6400000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '64,000', max: '6,400,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'rate_limit_bypass' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.test_strategy || 'test_strategy'}</label>
                                                                                            <select
                                                                                                value={val.params.test_strategy != null ? val.params.test_strategy : 'parameter_mutation'}
                                                                                                onChange={(e) => updateSubParams(cat, sub, 'test_strategy', e.target.value)}
                                                                                            >
                                                                                                <option value="ip_rotation">{t("dosOptions.ipRotation")}</option>
                                                                                                <option value="endpoint_variation">{t("dosOptions.endpointVariation")}</option>
                                                                                                <option value="parameter_mutation">{t("dosOptions.parameterMutation")}</option>
                                                                                            </select>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_context_overflow' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.phases || 'phases'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.phases === '' ? '' : (val.params.phases != null ? val.params.phases : 10)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'phases', e.target.value, 0)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'phases', e.target.value, 0)}
                                                                                                min="0"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_prompt_injection_dos' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.injection_type || 'injection_type'}</label>
                                                                                            <select
                                                                                                value={val.params.injection_type != null ? val.params.injection_type : 'direct'}
                                                                                                onChange={(e) => updateSubParams(cat, sub, 'injection_type', e.target.value)}
                                                                                            >
                                                                                                <option value="direct">{t("dosOptions.direct")}</option>
                                                                                                <option value="indirect">{t("dosOptions.indirect")}</option>
                                                                                            </select>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_repetitive_requests' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.pattern_type || 'pattern_type'}</label>
                                                                                            <select
                                                                                                value={val.params.pattern_type != null ? val.params.pattern_type : 'identical'}
                                                                                                onChange={(e) => updateSubParams(cat, sub, 'pattern_type', e.target.value)}
                                                                                            >
                                                                                                <option value="identical">{t("dosOptions.identical")}</option>
                                                                                                <option value="similar">{t("dosOptions.similar")}</option>
                                                                                                <option value="rotating">{t("dosOptions.rotating")}</option>
                                                                                            </select>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_session_hijacking_dos' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.sessions_per_ip || 'sessions_per_ip'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.sessions_per_ip === '' ? '' : (val.params.sessions_per_ip != null ? val.params.sessions_per_ip : 5)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'sessions_per_ip', e.target.value, 0)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'sessions_per_ip', e.target.value, 0)}
                                                                                                min="0"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_burst_attacks' && (
                                                                                        <>
                                                                                            <div>
                                                                                                <label>{paramLabels.burst_size || 'burst_size'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.burst_size === '' ? '' : (val.params.burst_size != null ? val.params.burst_size : 50)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'burst_size', e.target.value, 0)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'burst_size', e.target.value, 0)}
                                                                                                    min="0"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                            </div>
                                                                                            <div>
                                                                                                <label>{paramLabels.interval || 'interval'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.interval === '' ? '' : (val.params.interval != null ? val.params.interval : 2)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'interval', e.target.value, 0)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'interval', e.target.value, 0)}
                                                                                                    min="0"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                            </div>
                                                                                        </>
                                                                                    )}
                                                                                    {sub === 'test_slowloris_ai' && (
                                                                                        <>
                                                                                            <div>
                                                                                                <label>{paramLabels.chunks || 'chunks'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.chunks === '' ? '' : (val.params.chunks != null ? val.params.chunks : 5)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'chunks', e.target.value, 0)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'chunks', e.target.value, 0)}
                                                                                                    min="0"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                            </div>
                                                                                            <div>
                                                                                                <label>{paramLabels.delay || 'delay'}</label>
                                                                                                <input
                                                                                                    type="number"
                                                                                                    value={val.params.delay === '' ? '' : (val.params.delay != null ? val.params.delay : 2)}
                                                                                                    onChange={(e) => handleNumberParamChange(cat, sub, 'delay', e.target.value, 0)}
                                                                                                    onBlur={(e) => handleNumberParamBlur(cat, sub, 'delay', e.target.value, 0)}
                                                                                                    min="0"
                                                                                                />
                                                                                                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                            </div>
                                                                                        </>
                                                                                    )}
                                                                                    {sub === 'test_tool_execution_abuse' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.tool_calls || 'tool_calls'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.tool_calls === '' ? '' : (val.params.tool_calls != null ? val.params.tool_calls : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'tool_calls', e.target.value, 0)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'tool_calls', e.target.value, 0)}
                                                                                                min="0"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_model_poisoning_dos' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.poisoning_attempts || 'poisoning_attempts'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.poisoning_attempts === '' ? '' : (val.params.poisoning_attempts != null ? val.params.poisoning_attempts : 10)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'poisoning_attempts', e.target.value, 0)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'poisoning_attempts', e.target.value, 0)}
                                                                                                min="0"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );

                                                                    return (
                                                                        <>
                                                                            <div key={`${cat}-col1`} className="pm-category pm-target-card pm-dos-category">
                                                                                <div className="pm-subcategories">
                                                                                    {firstHalf.map(([sub, val]) => renderDosSub(sub, val))}
                                                                                </div>
                                                                            </div>
                                                                            {secondHalf.length > 0 && (
                                                                                <div key={`${cat}-col2`} className="pm-category pm-target-card pm-dos-category">
                                                                                    <div className="pm-subcategories">
                                                                                        {secondHalf.map(([sub, val]) => renderDosSub(sub, val))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                        {hasEnabledSubcategory('llm_dos') ? (
                                                            <FilePickingSection fileCategory="dos" />
                                                        ) : null}
                                                    </div>
                                                )}

                                                {currentPage === 7 && isPageVisible(7) && (
                                                    <div className="pm-tab-panel">
                                                        {/* Advance Agentic-AI Attacks */}
                                                        <div className="pm-categories-grid" style={{ marginTop: '24px' }}>
                                                            {(() => {
                                                                const cat = 'adv_agentic_ai_attacks';
                                                                const items = [
                                                                    { key: 'adv_crescendo' },
                                                                    { key: 'adv_trolley' },
                                                                    { key: 'adv_frame' },
                                                                    { key: 'adv_rec_pattern' },
                                                                    { key: 'adv_converter' }
                                                                ].map(({ key }) => {
                                                                    const label = (subcategoryLabels[cat] && subcategoryLabels[cat][key]) || key;
                                                                    const description = t(`agentic.${key}`);
                                                                    return { key, label, description };
                                                                });

                                                                const mid = Math.ceil(items.length / 2);
                                                                const firstHalf = items.slice(0, mid);
                                                                const secondHalf = items.slice(mid);

                                                                const renderAgenticRow = ({ key, label, description }) => (
                                                                    <div key={key} style={{ marginBottom: '14px' }}>
                                                                        <label className="pm-subcategory-label">
                                                                            {label}
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={getAgenticEnabled(key) || false}
                                                                                onChange={() => toggleAgenticSelection(key)}
                                                                            />
                                                                        </label>
                                                                        <div style={{ fontSize: '12px', color: getCurrentTheme() === 'light' ? '#6b7280' : '#9ca3af', marginTop: '4px', marginLeft: '0', lineHeight: 1.4 }}>
                                                                            {description}
                                                                        </div>
                                                                        {key === 'adv_crescendo' && (getAgenticEnabled('adv_crescendo') || hidingParams.has('adv_agentic_ai_attacks-adv_crescendo')) && (
                                                                            <div className={`pm-subcategory-params ${hidingParams.has('adv_agentic_ai_attacks-adv_crescendo') ? 'pm-hiding' : ''}`}>
                                                                                <div>
                                                                                    <label>{paramLabels.crescendo_prompt || 'Starting prompt'}</label>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={editing?.config?.adv_agentic_ai_attacks?.sub?.adv_crescendo?.params?.crescendo_prompt || ''}
                                                                                        onChange={(e) => updateSubParams('adv_agentic_ai_attacks', 'adv_crescendo', 'crescendo_prompt', e.target.value)}
                                                                                        placeholder={t("enterStartingPrompt")}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label>{paramLabels.stages || 'Stages'}</label>
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="1"
                                                                                        value={editing?.config?.adv_agentic_ai_attacks?.sub?.adv_crescendo?.params?.stages}
                                                                                        onChange={(e) => handleNumberParamChange('adv_agentic_ai_attacks', 'adv_crescendo', 'stages', e.target.value, 0)}
                                                                                        onBlur={(e) => handleNumberParamBlur('adv_agentic_ai_attacks', 'adv_crescendo', 'stages', e.target.value, 0)}
                                                                                    />
                                                                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <label>{paramLabels.requests_per_stage || 'Requests per Stage'}</label>
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="1"
                                                                                        value={editing?.config?.adv_agentic_ai_attacks?.sub?.adv_crescendo?.params?.requests_per_stage}
                                                                                        onChange={(e) => handleNumberParamChange('adv_agentic_ai_attacks', 'adv_crescendo', 'requests_per_stage', e.target.value, 0)}
                                                                                        onBlur={(e) => handleNumberParamBlur('adv_agentic_ai_attacks', 'adv_crescendo', 'requests_per_stage', e.target.value, 0)}
                                                                                    />
                                                                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <label>{paramLabels.intensity_multiplier || 'Intensity Multiplier'}</label>
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0.0"
                                                                                        step="0.1"
                                                                                        value={(() => {
                                                                                            const v = editing?.config?.adv_agentic_ai_attacks?.sub?.adv_crescendo?.params?.intensity_multiplier;
                                                                                            return v === '' ? '' : Number(v ?? 1.0).toFixed(1);
                                                                                        })()}
                                                                                        onChange={(e) => {
                                                                                            const { value } = e.target;
                                                                                            if (value === '') {
                                                                                                updateSubParams('adv_agentic_ai_attacks', 'adv_crescendo', 'intensity_multiplier', '');
                                                                                                return;
                                                                                            }
                                                                                            const parsed = parseFloat(value);
                                                                                            if (Number.isNaN(parsed)) return;
                                                                                            const clamped = Math.max(0, parsed);
                                                                                            updateSubParams('adv_agentic_ai_attacks', 'adv_crescendo', 'intensity_multiplier', Number(clamped.toFixed(1)));
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            const { value } = e.target;
                                                                                            if (value === '') return;
                                                                                            const parsed = parseFloat(value);
                                                                                            if (Number.isNaN(parsed)) {
                                                                                                updateSubParams('adv_agentic_ai_attacks', 'adv_crescendo', 'intensity_multiplier', 1.0);
                                                                                                return;
                                                                                            }
                                                                                            const clamped = Math.max(0, parsed);
                                                                                            updateSubParams('adv_agentic_ai_attacks', 'adv_crescendo', 'intensity_multiplier', Number(clamped.toFixed(1)));
                                                                                        }}
                                                                                    />
                                                                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0.0' })}</div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {key === 'adv_trolley' && (getAgenticEnabled('adv_trolley') || hidingParams.has('adv_agentic_ai_attacks-adv_trolley')) && (
                                                                            <div className={`pm-subcategory-params ${hidingParams.has('adv_agentic_ai_attacks-adv_trolley') ? 'pm-hiding' : ''}`}>
                                                                                <div>
                                                                                    <label>{paramLabels.trolley_prompt || 'Starting prompt'}</label>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={editing?.config?.adv_agentic_ai_attacks?.sub?.adv_trolley?.params?.trolley_prompt || ''}
                                                                                        onChange={(e) => updateSubParams('adv_agentic_ai_attacks', 'adv_trolley', 'trolley_prompt', e.target.value)}
                                                                                        placeholder={t("enterStartingPrompt")}
                                                                                    />
                                                                                </div>
                                                                                <div>
                                                                                    <label>{paramLabels.num_dilemmas || 'Number of Dilemmas'}</label>
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="1"
                                                                                        value={editing?.config?.adv_agentic_ai_attacks?.sub?.adv_trolley?.params?.num_dilemmas}
                                                                                        onChange={(e) => handleNumberParamChange('adv_agentic_ai_attacks', 'adv_trolley', 'num_dilemmas', e.target.value, 0)}
                                                                                        onBlur={(e) => handleNumberParamBlur('adv_agentic_ai_attacks', 'adv_trolley', 'num_dilemmas', e.target.value, 0)}
                                                                                    />
                                                                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <label>{paramLabels.recursion_depth || 'Recursion Depth'}</label>
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        step="1"
                                                                                        value={editing?.config?.adv_agentic_ai_attacks?.sub?.adv_trolley?.params?.recursion_depth}
                                                                                        onChange={(e) => handleNumberParamChange('adv_agentic_ai_attacks', 'adv_trolley', 'recursion_depth', e.target.value, 0)}
                                                                                        onBlur={(e) => handleNumberParamBlur('adv_agentic_ai_attacks', 'adv_trolley', 'recursion_depth', e.target.value, 0)}
                                                                                    />
                                                                                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.min', { min: '0' })}</div>
                                                                                </div>
                                                                                <div>
                                                                                    <label>{paramLabels.complexity_level || 'Complexity Level'}</label>
                                                                                    <select
                                                                                        value={editing?.config?.adv_agentic_ai_attacks?.sub?.adv_trolley?.params?.complexity_level || 'high'}
                                                                                        onChange={(e) => updateSubParams('adv_agentic_ai_attacks', 'adv_trolley', 'complexity_level', e.target.value)}
                                                                                    >
                                                                                        <option value="high">{t("dosOptions.high")}</option>
                                                                                        <option value="extreme">{t("dosOptions.extreme")}</option>
                                                                                    </select>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {key === 'adv_frame' && (getAgenticEnabled('adv_frame') || hidingParams.has('adv_agentic_ai_attacks-adv_frame')) && (
                                                                            <div className={`pm-subcategory-params ${hidingParams.has('adv_agentic_ai_attacks-adv_frame') ? 'pm-hiding' : ''}`}>
                                                                                <input
                                                                                    ref={frameFileInputRef}
                                                                                    type="file"
                                                                                    accept="*/*"
                                                                                    style={{ display: 'none' }}
                                                                                    onChange={(e) => {
                                                                                        const file = e.target.files?.[0];
                                                                                        e.target.value = '';
                                                                                        if (!file) return;
                                                                                        if (file.size > 1024 * 1024) {
                                                                                            setErrorPopup({ show: true, message: t('validation.frameFileTooLarge') });
                                                                                            return;
                                                                                        }

                                                                                        (async () => {
                                                                                            try {
                                                                                                const csrfToken = await getCsrfToken();
                                                                                                const formData = new FormData();
                                                                                                formData.append('test_file_category', 'frame');
                                                                                                formData.append('files', file);

                                                                                                const response = await fetch(getApiUrl('/api/internal/test-files/upload/'), {
                                                                                                    method: 'POST',
                                                                                                    credentials: 'include',
                                                                                                    headers: {
                                                                                                        'X-CSRFToken': csrfToken
                                                                                                    },
                                                                                                    body: formData
                                                                                                });

                                                                                                if (!response.ok) {
                                                                                                    const errorData = await response.json().catch(() => ({}));
                                                                                                    throw new Error(errorData.detail || errorData.message || `HTTP ${response.status}`);
                                                                                                }

                                                                                                const data = await response.json().catch(() => ({}));
                                                                                                if (!data.success) {
                                                                                                    throw new Error(data.detail || data.message || t('errors.uploadFailed'));
                                                                                                }

                                                                                                const uploaded = Array.isArray(data.uploaded_files) ? data.uploaded_files : [];
                                                                                                if (uploaded.length > 0) {
                                                                                                    // For FRAME keep latest upload set
                                                                                                    setFrameFiles(uploaded);
                                                                                                }
                                                                                            } catch (err) {
                                                                                                console.error('Error uploading FRAME file:', err);
                                                                                                setErrorPopup({
                                                                                                    show: true,
                                                                                                    message: t('errors.failedToUploadFrame', { message: err.message || t('errors.unknownError') })
                                                                                                });
                                                                                            }
                                                                                        })();
                                                                                    }}
                                                                                />
                                                                                <div>
                                                                                    <label>{t("inputFile")}</label>
                                                                                    {frameFiles.length > 0 ? (
                                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                                                            <span style={{ color: getCurrentTheme() === 'light' ? '#374151' : '#9ca3af', fontSize: '14px' }}>
                                                                                                {frameFiles[0].file_name}
                                                                                            </span>
                                                                                            <button
                                                                                                type="button"
                                                                                                className="pm-save-btn"
                                                                                                style={{ padding: '6px 14px', fontSize: '13px' }}
                                                                                                onClick={() => frameFileInputRef.current?.click()}
                                                                                            >
                                                                                                {t("replace")}
                                                                                            </button>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="pm-save-btn"
                                                                                            style={{ padding: '6px 14px', fontSize: '13px' }}
                                                                                            onClick={() => frameFileInputRef.current?.click()}
                                                                                        >
                                                                                            {t("chooseFile")}
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                        {key === 'adv_converter' && (getAgenticEnabled('adv_converter') || hidingParams.has('adv_agentic_ai_attacks-adv_converter')) && (
                                                                            <div className={`pm-subcategory-params ${hidingParams.has('adv_agentic_ai_attacks-adv_converter') ? 'pm-hiding' : ''}`}>
                                                                                {[
                                                                                    { key: 'base64_converter', paramKey: 'base64_conv' },
                                                                                    { key: 'single_char_converter', paramKey: 'single_char_conv' },
                                                                                    { key: 'repeat_token_converter', paramKey: 'repeat_conv' },
                                                                                    { key: 'caesar_converter', paramKey: 'caesar_conv' },
                                                                                    { key: 'leetspeak_converter', paramKey: 'leet_conv' },
                                                                                    { key: 'unicode_confusable_converter', paramKey: 'unicode_conv' }
                                                                                ].map(({ key: convKey, paramKey }) => (
                                                                                    <div key={convKey} className="pm-agentic-converter-row">
                                                                                        <label style={{ marginBottom: 0, fontSize: '13px', fontWeight: 600 }}>{paramLabels[paramKey] || paramKey}</label>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={editing?.config?.adv_agentic_ai_attacks?.sub?.adv_converter?.params?.[paramKey] || false}
                                                                                            onChange={() => updateSubParams('adv_agentic_ai_attacks', 'adv_converter', paramKey, !(editing?.config?.adv_agentic_ai_attacks?.sub?.adv_converter?.params?.[paramKey] || false))}
                                                                                        />
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );

                                                                return (
                                                                    <>
                                                                        <div className="pm-category pm-target-card pm-agentic-category" style={{ opacity: 0.9 }}>
                                                                            <div className="pm-subcategories">
                                                                                {firstHalf.map(renderAgenticRow)}
                                                                            </div>
                                                                        </div>
                                                                        {secondHalf.length > 0 && (
                                                                            <div className="pm-category pm-target-card pm-agentic-category" style={{ opacity: 0.9 }}>
                                                                                <div className="pm-subcategories">
                                                                                    {secondHalf.map(renderAgenticRow)}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </div>
                                                        {/* File picking not used on this page */}
                                                    </div>
                                                )}

                                                {currentPage === 8 && isPageVisible(8) && (
                                                    <div className="pm-tab-panel">
                                                        <div
                                                            style={{
                                                                marginTop: '12px',
                                                                marginBottom: '16px',
                                                                color: getCurrentTheme() === 'light' ? '#6b7280' : '#9ca3af',
                                                                fontSize: '12px',
                                                                fontStyle: 'italic',
                                                                textAlign: 'center',
                                                                lineHeight: 1.5
                                                            }}
                                                        >
                                                            Note: These MCP tests may cause disruption in MCP services. It is highly recommended to run these tests only in non-production environments.
                                                        </div>
                                                        {/* --- MCP Agent category box --- */}
                                                        <div className="pm-categories-grid" style={{ marginTop: '24px' }}>
                                                            {(() => {
                                                                const cat = 'mcp_agent';
                                                                if (editing.config[cat] && typeof editing.config[cat] === 'object' && editing.config[cat].sub && typeof editing.config[cat].sub === 'object') {
                                                                    const data = editing.config[cat];
                                                                    const entries = Object.entries(data.sub || {});
                                                                    const mid = Math.ceil(entries.length / 2);
                                                                    const firstHalf = entries.slice(0, mid);
                                                                    const secondHalf = entries.slice(mid);

                                                                    const renderMcpSub = (sub, val) => (
                                                                        <div key={sub}>
                                                                            <label className="pm-subcategory-label">
                                                                                {(subcategoryLabels[cat] && subcategoryLabels[cat][sub]) || sub}
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={val.enabled}
                                                                                    onChange={() => toggleSub(cat, sub)}
                                                                                />
                                                                            </label>
                                                                            {cat === 'mcp_agent' && val.params && (val.enabled || hidingParams.has(`mcp_agent-${sub}`)) && (
                                                                                <div className={`pm-subcategory-params ${hidingParams.has(`mcp_agent-${sub}`) ? 'pm-hiding' : ''}`}>
                                                                                    {sub === 'resource_exhaustion_mcpa' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.concurrent_resource_requests || 'concurrent_resource_requests'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.concurrent_resource_requests === '' ? '' : (val.params.concurrent_resource_requests != null ? val.params.concurrent_resource_requests : 160)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'concurrent_resource_requests', e.target.value, 0, 2300)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'concurrent_resource_requests', e.target.value, 0, 2300)}
                                                                                                min="0"
                                                                                                max="2300"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '2,300' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'protocol_violation' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.number_of_protocol_requests || 'number_of_protocol_requests'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.number_of_protocol_requests === '' ? '' : (val.params.number_of_protocol_requests != null ? val.params.number_of_protocol_requests : 60)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'number_of_protocol_requests', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'number_of_protocol_requests', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'tool_abuse' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.number_of_tool_requests || 'number_of_tool_requests'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.number_of_tool_requests === '' ? '' : (val.params.number_of_tool_requests != null ? val.params.number_of_tool_requests : 200)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'number_of_tool_requests', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'number_of_tool_requests', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'memory_leak' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.number_of_iterations || 'number_of_iterations'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.number_of_iterations === '' ? '' : (val.params.number_of_iterations != null ? val.params.number_of_iterations : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'number_of_iterations', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'number_of_iterations', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'session_hijack' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.number_of_sessions || 'number_of_sessions'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.number_of_sessions === '' ? '' : (val.params.number_of_sessions != null ? val.params.number_of_sessions : 40)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'number_of_sessions', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'number_of_sessions', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'context_overflow' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.context_overflow_count || 'context_overflow_count'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.context_overflow_count === '' ? '' : (val.params.context_overflow_count != null ? val.params.context_overflow_count : 0)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'context_overflow_count', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'context_overflow_count', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'malicious_tool_calls' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.number_of_malicious_requests || 'number_of_malicious_requests'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.number_of_malicious_requests === '' ? '' : (val.params.number_of_malicious_requests != null ? val.params.number_of_malicious_requests : 100)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'number_of_malicious_requests', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'number_of_malicious_requests', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_path_traversal' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.path_trav_attacks || 'path_trav_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.path_trav_attacks === '' ? '' : (val.params.path_trav_attacks != null ? val.params.path_trav_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'path_trav_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'path_trav_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_command_injection' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.cmd_inj_attacks || 'cmd_inj_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.cmd_inj_attacks === '' ? '' : (val.params.cmd_inj_attacks != null ? val.params.cmd_inj_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'cmd_inj_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'cmd_inj_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_sql_injection' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.sql_inj_attacks || 'sql_inj_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.sql_inj_attacks === '' ? '' : (val.params.sql_inj_attacks != null ? val.params.sql_inj_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'sql_inj_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'sql_inj_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_data_exfiltration' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.data_exfil_attacks || 'data_exfil_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.data_exfil_attacks === '' ? '' : (val.params.data_exfil_attacks != null ? val.params.data_exfil_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'data_exfil_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'data_exfil_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_prompt_injection_mcpa' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.prompt_inj_attacks || 'prompt_inj_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.prompt_inj_attacks === '' ? '' : (val.params.prompt_inj_attacks != null ? val.params.prompt_inj_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'prompt_inj_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'prompt_inj_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_auth_bypass' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.auth_bypass_attacks || 'auth_bypass_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.auth_bypass_attacks === '' ? '' : (val.params.auth_bypass_attacks != null ? val.params.auth_bypass_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'auth_bypass_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'auth_bypass_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_malicious_content' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.mal_cont_attacks || 'mal_cont_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.mal_cont_attacks === '' ? '' : (val.params.mal_cont_attacks != null ? val.params.mal_cont_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'mal_cont_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'mal_cont_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_session_hijacking_mcpa' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.session_hijack_attacks || 'session_hijack_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.session_hijack_attacks === '' ? '' : (val.params.session_hijack_attacks != null ? val.params.session_hijack_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'session_hijack_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'session_hijack_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_tool_misuse' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.tool_misuse_attacks || 'tool_misuse_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.tool_misuse_attacks === '' ? '' : (val.params.tool_misuse_attacks != null ? val.params.tool_misuse_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'tool_misuse_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'tool_misuse_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_generic_injection' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.gen_inj_attacks || 'gen_inj_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.gen_inj_attacks === '' ? '' : (val.params.gen_inj_attacks != null ? val.params.gen_inj_attacks : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'gen_inj_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'gen_inj_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_resource_exhaustion' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.rsc_ex_attacks || 'rsc_ex_attacks'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.rsc_ex_attacks === '' ? '' : (val.params.rsc_ex_attacks != null ? val.params.rsc_ex_attacks : 10)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'rsc_ex_attacks', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'rsc_ex_attacks', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                    {sub === 'test_benign_requests' && (
                                                                                        <div>
                                                                                            <label>{paramLabels.benign_reqs_num || 'benign_reqs_num'}</label>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={val.params.benign_reqs_num === '' ? '' : (val.params.benign_reqs_num != null ? val.params.benign_reqs_num : 20)}
                                                                                                onChange={(e) => handleNumberParamChange(cat, sub, 'benign_reqs_num', e.target.value, 0, 15000)}
                                                                                                onBlur={(e) => handleNumberParamBlur(cat, sub, 'benign_reqs_num', e.target.value, 0, 15000)}
                                                                                                min="0"
                                                                                                max="15000"
                                                                                            />
                                                                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>{t('hints.range', { min: '0', max: '15,000' })}</div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );

                                                                    return (
                                                                        <>
                                                                            <div key={`${cat}-col1`} className="pm-category pm-target-card pm-mcp-category">
                                                                                <div className="pm-subcategories">
                                                                                    {firstHalf.map(([sub, val]) => renderMcpSub(sub, val))}
                                                                                </div>
                                                                            </div>
                                                                            {secondHalf.length > 0 && (
                                                                                <div key={`${cat}-col2`} className="pm-category pm-target-card pm-mcp-category">
                                                                                    <div className="pm-subcategories">
                                                                                        {secondHalf.map(([sub, val]) => renderMcpSub(sub, val))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                        {hasEnabledSubcategory('mcp_agent') ? (
                                                            <FilePickingSection fileCategory="mcp" />
                                                        ) : null}
                                                    </div>
                                                )}

                                                {currentPage === 9 && isPageVisible(9) && (
                                                    <div className="pm-tab-panel">
                                                        <div
                                                            style={{
                                                                marginTop: '12px',
                                                                marginBottom: '16px',
                                                                color: getCurrentTheme() === 'light' ? '#6b7280' : '#9ca3af',
                                                                fontSize: '12px',
                                                                fontStyle: 'italic',
                                                                textAlign: 'center',
                                                                lineHeight: 1.5
                                                            }}
                                                        >
                                                            These tests can be run against a production MCP server. However, use caution if MCP server is under heavy use.
                                                        </div>
                                                        <div className="pm-categories-grid" style={{ marginTop: '24px' }}>
                                                            {(() => {
                                                                const cat = 'safe_mcp_tests';
                                                                if (editing.config[cat] && typeof editing.config[cat] === 'object' && editing.config[cat].sub && typeof editing.config[cat].sub === 'object') {
                                                                    const data = editing.config[cat];
                                                                    const entries = Object.entries(data.sub || {});
                                                                    const mid = Math.ceil(entries.length / 2);
                                                                    const firstHalf = entries.slice(0, mid);
                                                                    const secondHalf = entries.slice(mid);

                                                                    const renderSafeMcpSub = (sub, val) => (
                                                                        <label key={sub} className="pm-subcategory-label">
                                                                            {(subcategoryLabels[cat] && subcategoryLabels[cat][sub]) || sub}
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={val.enabled}
                                                                                onChange={() => toggleSub(cat, sub)}
                                                                            />
                                                                        </label>
                                                                    );

                                                                    return (
                                                                        <>
                                                                            <div className="pm-category pm-target-card">
                                                                                <div className="pm-subcategories">
                                                                                    {firstHalf.map(([sub, val]) => renderSafeMcpSub(sub, val))}
                                                                                </div>
                                                                            </div>
                                                                            {secondHalf.length > 0 && (
                                                                                <div className="pm-category pm-target-card">
                                                                                    <div className="pm-subcategories">
                                                                                        {secondHalf.map(([sub, val]) => renderSafeMcpSub(sub, val))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </div>
                                                    </div>
                                                )}

                                                {currentPage === 10 && isPageVisible(10) && (
                                                    <div className="pm-tab-panel">
                                                        <div className="pm-rto-centered">
                                                            {/* --- RTO category box --- */}
                                                            <div className="pm-categories-grid" style={{ marginTop: '24px' }}>
                                                                {(() => {
                                                                    const cat = 'rto_tests';
                                                                    if (editing.config[cat] && typeof editing.config[cat] === 'object' && editing.config[cat].sub && typeof editing.config[cat].sub === 'object') {
                                                                        const data = editing.config[cat];
                                                                        const entries = Object.entries(data.sub || {});
                                                                        const mid = Math.ceil(entries.length / 2);
                                                                        const firstHalf = entries.slice(0, mid);
                                                                        const secondHalf = entries.slice(mid);

                                                                        const renderRtoSub = (sub, val) => (
                                                                            <label key={sub} className="pm-subcategory-label">
                                                                                {(subcategoryLabels[cat] && subcategoryLabels[cat][sub]) || sub}
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={val.enabled}
                                                                                    onChange={() => toggleSub(cat, sub)}
                                                                                />
                                                                            </label>
                                                                        );

                                                                        return (
                                                                            <>
                                                                                <div className="pm-category pm-target-card">
                                                                                    <div className="pm-subcategories">
                                                                                        {firstHalf.map(([sub, val]) => renderRtoSub(sub, val))}
                                                                                    </div>
                                                                                </div>
                                                                                {secondHalf.length > 0 && (
                                                                                    <div className="pm-category pm-target-card">
                                                                                        <div className="pm-subcategories">
                                                                                            {secondHalf.map(([sub, val]) => renderRtoSub(sub, val))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })()}
                                                            </div>
                                                            {/* Average Response Time Selection Box */}
                                                            <div className="pm-target-card" style={{ marginTop: '24px', padding: '16px' }}>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                                    <label style={{
                                                                        fontSize: '14px',
                                                                        fontWeight: '500',
                                                                        color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff'
                                                                    }}>
                                                                        {t("rto.averageResponseTime")}
                                                                    </label>
                                                                    <select
                                                                        value={editing?.config?.rto_tests?.average_response_time || 'overall'}
                                                                        onChange={(e) => setEditing(prev => ({
                                                                            ...prev,
                                                                            config: {
                                                                                ...prev.config,
                                                                                rto_tests: {
                                                                                    ...prev.config.rto_tests,
                                                                                    average_response_time: e.target.value
                                                                                }
                                                                            }
                                                                        }))}
                                                                        style={{
                                                                            padding: '8px 36px 8px 12px',
                                                                            fontSize: '14px',
                                                                            borderRadius: '6px',
                                                                            border: getCurrentTheme() === 'light' ? '1px solid #d1d5db' : '1px solid #374151',
                                                                            backgroundColor: getCurrentTheme() === 'light' ? '#ffffff' : '#111827',
                                                                            color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                                            width: '100%',
                                                                            appearance: 'none',
                                                                            WebkitAppearance: 'none',
                                                                            MozAppearance: 'none',
                                                                            backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E\")",
                                                                            backgroundRepeat: 'no-repeat',
                                                                            backgroundPosition: 'right 12px center',
                                                                            backgroundSize: '12px',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                    >
                                                                        <option value="overall">{t("rto.overall")}</option>
                                                                        <option value="reg">{t("rto.regularPrompts")}</option>
                                                                        <option value="mid">{t("rto.mediumPrompts")}</option>
                                                                        <option value="large">{t("rto.largePrompts")}</option>
                                                                        <option value="doc">{t("rto.documents")}</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {hasEnabledSubcategory('rto_tests') ? (
                                                            <FilePickingSection fileCategory="rto" />
                                                        ) : null}
                                                    </div>
                                                )}

                                                {currentPage === 11 && isPageVisible(11) && (
                                                    <div className="pm-tab-panel">
                                                        <div className="pm-categories-grid" style={{ marginTop: '24px', gridTemplateColumns: '1fr' }}>
                                                            {(() => {
                                                                const cat = 'regulation_compliance_testing';
                                                                if (!editing.config[cat] || typeof editing.config[cat] !== 'object') return null;
                                                                const data = editing.config[cat];
                                                                return (
                                                                    <div key={cat} className="pm-category pm-target-card">
                                                                        <label className="pm-category-label">
                                                                            {categoryLabels[cat]}
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={data.enabled}
                                                                                onChange={() => toggleCategory(cat)}
                                                                            />
                                                                        </label>
                                                                        <div
                                                                            style={{
                                                                                marginTop: '8px',
                                                                                fontSize: '13px',
                                                                                color: getCurrentTheme() === 'light' ? '#4b5563' : '#9ca3af'
                                                                            }}
                                                                        >
                                                                            {t("promptsPage.multiDatasetHint")}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>

                                                        {editing?.config?.regulation_compliance_testing?.enabled ? (
                                                            <FilePickingSection fileCategory="rc_prompts" />
                                                        ) : null}

                                                        {editing?.config?.regulation_compliance_testing?.enabled ? (
                                                            <>
                                                                <div style={{ marginBottom: '12px', marginTop: '72px', textAlign: 'center' }}>
                                                                    <label style={{
                                                                        fontSize: '14px',
                                                                        fontWeight: '600',
                                                                        color: getCurrentTheme() === 'light' ? '#000000' : '#ffffff',
                                                                        display: 'block'
                                                                    }}>
                                                                        {t("promptsPage.customFilesTitle")}
                                                                    </label>
                                                                    <div style={{
                                                                        marginTop: '4px',
                                                                        fontSize: '12px',
                                                                        color: getCurrentTheme() === 'light' ? '#6b7280' : '#9ca3af'
                                                                    }}>
                                                                        {t("promptsPage.customFilesHint")}
                                                                    </div>
                                                                </div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                                    {REGULATION_COMPLIANCE_GRID_CATEGORIES.map((catKey) => {
                                                                        const categoryEntry = allPossibleCategories.find((item) => item.category === catKey);
                                                                        const subcats = Array.isArray(categoryEntry?.subcategories) ? categoryEntry.subcategories : [];
                                                                        return (
                                                                            <div
                                                                                key={catKey}
                                                                                style={{
                                                                                    border: getCurrentTheme() === 'light' ? '1px solid #d1d5db' : '1px solid #374151',
                                                                                    borderRadius: '8px',
                                                                                    padding: '10px',
                                                                                    backgroundColor: getCurrentTheme() === 'light' ? '#f9fafb' : '#111827'
                                                                                }}
                                                                            >
                                                                                <div style={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    gap: '2px',
                                                                                    marginBottom: '8px'
                                                                                }}>
                                                                                    <span style={{
                                                                                        fontSize: '13px',
                                                                                        fontWeight: '700',
                                                                                        color: getCurrentTheme() === 'light' ? '#111827' : '#f3f4f6'
                                                                                    }}>
                                                                                        {categoryLabels[catKey] || catKey}
                                                                                    </span>
                                                                                    <span style={{
                                                                                        fontSize: '11px',
                                                                                        color: getCurrentTheme() === 'light' ? '#9ca3af' : '#6b7280',
                                                                                        fontFamily: 'monospace'
                                                                                    }}>
                                                                                        {catKey}
                                                                                    </span>
                                                                                </div>
                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                                    {subcats.map((subKey) => (
                                                                                        <div
                                                                                            key={subKey}
                                                                                            style={{
                                                                                                display: 'flex',
                                                                                                flexDirection: 'column',
                                                                                                gap: '2px',
                                                                                                padding: '6px 8px',
                                                                                                borderRadius: '6px',
                                                                                                backgroundColor: getCurrentTheme() === 'light' ? '#f3f4f6' : '#1f2937',
                                                                                                border: getCurrentTheme() === 'light' ? '1px solid #e5e7eb' : '1px solid #374151'
                                                                                            }}
                                                                                        >
                                                                                            <span style={{
                                                                                                fontSize: '12px',
                                                                                                color: getCurrentTheme() === 'light' ? '#374151' : '#d1d5db'
                                                                                            }}>
                                                                                                {subcategoryLabelsMap[subKey] || subKey}
                                                                                            </span>
                                                                                            <span style={{
                                                                                                fontSize: '11px',
                                                                                                color: getCurrentTheme() === 'light' ? '#9ca3af' : '#6b7280',
                                                                                                fontFamily: 'monospace'
                                                                                            }}>
                                                                                                {subKey}
                                                                                            </span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                )}

                                                {currentPage === 12 && isPageVisible(12) && (
                                                    <div className="pm-tab-panel pm-tab-panel--page2 pm-tab-panel--compact">
                                                        <div
                                                            className="pm-tab2-content"
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                padding: '40px 24px',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            <div
                                                                className="pm-dos-category"
                                                                style={{ width: '100%', maxWidth: '520px', marginBottom: '24px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '16px' }}
                                                            >
                                                                <div className="pm-subcategory-params" style={{ animation: 'none', margin: 0 }}>
                                                                    <div>
                                                                        <label>{t("fields.retryAttempts")}</label>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            max="25"
                                                                            value={FIXED_RETRY_ATTEMPTS}
                                                                            disabled
                                                                            style={{ opacity: 0.5 }}
                                                                        />
                                                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                                                                            {t('hints.range', { min: '0', max: '25' })}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                {profileType !== 'dos_tester' && (
                                                                <>
                                                                <div className="pm-subcategory-params" style={{ animation: 'none', margin: 0 }}>
                                                                    <div>
                                                                        <label style={{ margin: 0 }}>{t("fields.concurrentTesting")}</label>
                                                                        <select
                                                                            value={throttleSelectValue(editing?.concurrent_testing)}
                                                                            onChange={(e) => {
                                                                                const selected = e.target.value;
                                                                                setEditing((prev) => ({
                                                                                    ...prev,
                                                                                    concurrent_testing: selected === 'auto'
                                                                                        ? { auto_select: true, value: prev?.concurrent_testing?.value ?? 1 }
                                                                                        : { auto_select: false, value: Number(selected) }
                                                                                }));
                                                                            }}
                                                                        >
                                                                            <option value="auto">{t("fields.auto")}</option>
                                                                            {CONCURRENT_TESTING_OPTIONS.map((n) => (
                                                                                <option key={n} value={String(n)}>{n}</option>
                                                                            ))}
                                                                        </select>
                                                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                                                                            {t('fields.concurrentTestingDesc')}
                                                                        </div>
                                                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                                                                            {t('hints.range', { min: '1', max: '10' })}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div className="pm-subcategory-params" style={{ animation: 'none', margin: 0 }}>
                                                                    <div>
                                                                        <label style={{ margin: 0 }}>{t("fields.promptsPerSec")}</label>
                                                                        <select
                                                                            value={throttleSelectValue(editing?.prompts_per_sec)}
                                                                            onChange={(e) => {
                                                                                const selected = e.target.value;
                                                                                setEditing((prev) => ({
                                                                                    ...prev,
                                                                                    prompts_per_sec: selected === 'auto'
                                                                                        ? { auto_select: true, value: prev?.prompts_per_sec?.value ?? 1 }
                                                                                        : { auto_select: false, value: Number(selected) }
                                                                                }));
                                                                            }}
                                                                        >
                                                                            <option value="auto">{t("fields.auto")}</option>
                                                                            {PROMPTS_PER_SEC_OPTIONS.map((n) => (
                                                                                <option key={n} value={String(n)}>{n}</option>
                                                                            ))}
                                                                        </select>
                                                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                                                                            {t('fields.promptsPerSecDesc')}
                                                                        </div>
                                                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                                                                            {t('hints.range', { min: '1', max: '50' })}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                </>
                                                                )}
                                                            </div>
                                                            <button
                                                                className="pm-save-btn"
                                                                onClick={handleFinishProfile}
                                                                disabled={loading}
                                                                style={{
                                                                    padding: '12px 32px',
                                                                    fontSize: '16px',
                                                                    fontWeight: 600,
                                                                    opacity: loading ? 0.5 : 1
                                                                }}
                                                            >
                                                                {loading ? (
                                                                    <>
                                                                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                                                                        {t("saving")}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {t("finish")}
                                                                        <i className="fas fa-check" style={{ marginLeft: '8px' }}></i>
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Bottom Navigation */}
                                        <div className="pm-modal-actions">
                                            <div className="pm-bottom-nav">
                                                {/* Page 1: Cancel and Next Arrow */}
                                                {currentPage === 1 && isPageVisible(1) && (
                                                    <>
                                                        <button
                                                            className="pm-close-btn"
                                                            onClick={() => setEditing(null)}
                                                            disabled={loading}
                                                        >
                                                            {t("cancel")}
                                                        </button>
                                                        <button
                                                            className="pm-save-btn pm-nav-arrow"
                                                            onClick={() => setCurrentPage(getNextVisiblePage(currentPage))}
                                                            title={t("next")}
                                                            disabled={loading || getNextVisiblePage(currentPage) === currentPage || !canGoNext()}
                                                        >
                                                            <i className="fas fa-chevron-right"></i>
                                                        </button>
                                                    </>
                                                )}

                                                {/* Middle Pages: Back Arrow and Next Arrow */}
                                                {(() => {
                                                    const visiblePages = getVisiblePages();
                                                    const isFirstVisible = visiblePages[0] === currentPage;
                                                    const isLastVisible = visiblePages[visiblePages.length - 1] === currentPage;
                                                    const isMiddlePage = !isFirstVisible && !isLastVisible && isPageVisible(currentPage);

                                                    if (isMiddlePage) {
                                                        return (
                                                            <>
                                                                <button
                                                                    className="pm-close-btn pm-nav-arrow"
                                                                    onClick={() => setCurrentPage(getPreviousVisiblePage(currentPage))}
                                                                    title={t("previous")}
                                                                    disabled={loading}
                                                                >
                                                                    <i className="fas fa-chevron-left"></i>
                                                                </button>
                                                                <button
                                                                    className="pm-save-btn pm-nav-arrow"
                                                                    onClick={() => setCurrentPage(getNextVisiblePage(currentPage))}
                                                                    title={t("next")}
                                                                    disabled={loading || getNextVisiblePage(currentPage) === currentPage || !canGoNext()}
                                                                >
                                                                    <i className="fas fa-chevron-right"></i>
                                                                </button>
                                                            </>
                                                        );
                                                    }
                                                    return null;
                                                })()}

                                                {/* Last Page: Back Arrow only */}
                                                {(() => {
                                                    const visiblePages = getVisiblePages();
                                                    const isLastVisible = visiblePages[visiblePages.length - 1] === currentPage && currentPage !== 1;

                                                    if (isLastVisible) {
                                                        return (
                                                            <button
                                                                className="pm-close-btn pm-nav-arrow"
                                                                onClick={() => setCurrentPage(getPreviousVisiblePage(currentPage))}
                                                                title={t("previous")}
                                                                disabled={loading}
                                                            >
                                                                <i className="fas fa-chevron-left"></i>
                                                            </button>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </main>
            </div>

            <ConfirmModal
                open={pendingDeleteId != null}
                title={t("table.deleteTitle")}
                message={t("errors.deleteConfirm")}
                confirmLabel={t("table.deleteTitle")}
                cancelLabel={t("cancel")}
                zIndex={2100}
                onCancel={() => setPendingDeleteId(null)}
                onConfirm={() => {
                    const id = pendingDeleteId;
                    setPendingDeleteId(null);
                    if (id != null) deleteProfile(id);
                }}
            />

            <StatusResultModal
                open={statusModal.open}
                type={statusModal.type}
                message={statusModal.message}
                zIndex={2100}
                onClose={() =>
                    setStatusModal({ open: false, type: "success", message: "" })
                }
            />

            {/* Error Popup */}
            {errorPopup.show && (
                <div className="pm-error-popup-overlay" onClick={() => setErrorPopup({ show: false, message: "" })}>
                    <div className="pm-error-popup" onClick={(e) => e.stopPropagation()}>
                        <div className="pm-error-popup-header">
                            <h4>{t("error")}</h4>
                            <button
                                className="pm-error-close-btn"
                                onClick={() => setErrorPopup({ show: false, message: "" })}
                            >
                                ×
                            </button>
                        </div>
                        <div className="pm-error-popup-content">
                            <p>{errorPopup.message}</p>
                        </div>
                        <div className="pm-error-popup-actions">
                            <button
                                className="pm-error-ok-btn"
                                onClick={() => setErrorPopup({ show: false, message: "" })}
                            >
                                {t("ok")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* AI-DoS Precautions Modal */}
            {showDosPrecautions && (
                <div className="pm-error-popup-overlay" onClick={() => setShowDosPrecautions(false)}>
                    <div className="pm-error-popup pm-dos-precautions-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pm-error-popup-header">
                            <h4>{t("warnings")}</h4>
                            <button
                                className="pm-error-close-btn"
                                onClick={() => setShowDosPrecautions(false)}
                                aria-label={t("close")}
                            >
                                ×
                            </button>
                        </div>
                        <div className="pm-error-popup-content pm-dos-precautions-content">
                                                        <ol className="pm-dos-precautions-list">
                                <li>
                                    {t("dosPrecautions.item1")}
                                </li>
                                <li>
                                    {t("dosPrecautions.item2")}
                                </li>
                                <li>
                                    {t("dosPrecautions.item3")}
                                </li>
                                <li>
                                    {t("dosPrecautions.item4")}
                                </li>
                            </ol>
                        </div>
                        <div className="pm-error-popup-actions">
                            <button
                                className="pm-error-ok-btn"
                                onClick={() => setShowDosPrecautions(false)}
                            >
                                {t("ok")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
